// Candidate X-Ray Template — admin-only preview (Methodology v3.7)
// Route:  /admin/xray-template-preview
// Access: hardcoded backend allowlist {"joshua@thcohq.com"} only (case-insensitive).
// Sample content for Alex Morgan is hardcoded verbatim. No DB reads, no candidate data.
import React, { useEffect, useState, createContext, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  StrategicAssessmentDiagram,
  BuilderPathDiagram,
  InvestmentTrainingRoadmap,
  TechnicalCompetencyTable,
  RecommendationLayer,
} from "./XrayTemplateExtensions";
import { applyExecutiveSafetyNet, isExecutiveRole } from "./_execProtected";
import { isSbblHeld } from "./_sbblHold";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// -------------------- Palette --------------------
const C = {
  forest: "#00583D",
  forestDark: "#003B28",
  forestPale: "#E8F0EC",
  forestMid: "#B8D4C7",
  forestMid2: "#3B7A5C",   // Emerging Builder tier badge bg
  forestLight: "#00674A",  // Score Composition donut centre band (promoted from inline per v4.0 §12.2)
  mutedRose: "#A85D5D",    // Restructuring Candidate tier badge bg
  amberDarker: "#B07E2C",  // Support Developer (PRR) tier badge bg
  greySoft:    "#D8DDE3",  // No Data tier + DEFER (PLATFORM ISSUE) verdict bg
  charcoal: "#1F2933",
  charcoalSoft: "#4A5568",
  gold: "#C28F2C",
  goldDark: "#9B6F1E",
  goldLight: "#F5E6C4",
  amberSoft: "#FDF6E3",
  ivory: "#FAFAF7",   // v4.0 §12.2 canonical (was #FAF8F3, off by 2 hex digits)
  line: "#D8DDE3",
  lineSoft: "#E8EAEE",
  muted: "#6B7280",
  mutedLight: "#C5CAD2",
};

// Tier → badge colour map (header strip).
// Any tier rendered later will auto-resolve once real data is wired.
const TIER_BADGE = {
  "Core Builder":                                 { bg: C.forest,      fg: "#FFFFFF" },
  "Emerging Builder":                             { bg: C.forestMid2,  fg: "#FFFFFF" },
  "Support Developer":                            { bg: C.gold,        fg: C.charcoal },
  "Support Developer (PRR)":                      { bg: C.amberDarker, fg: "#FFFFFF" },
  "Restructuring Candidate":                      { bg: C.mutedRose,   fg: "#FFFFFF" },
  "No Data":                                      { bg: C.greySoft,    fg: C.charcoal },
};

// Assessment Verdict → pill colour map (Verdict panel)
const VERDICT_PILL = {
  "STABILISE & MONITOR":     { bg: C.forest,     fg: "#FFFFFF" },
  "REDEPLOY":                { bg: C.gold,       fg: C.charcoal },
  "TRANSITION":              { bg: C.mutedRose,  fg: "#FFFFFF" },
  "DEFER (PLATFORM ISSUE)":  { bg: C.greySoft,   fg: C.charcoal },
};

// Talent Disposition → pill colour map (v4.0 §13.1)
const DISPOSITION_PILL = {
  "Retain & Elevate": { bg: C.forest,     fg: "#FFFFFF" },
  "Develop":          { bg: C.forestMid2, fg: "#FFFFFF" },
  "Reassign":         { bg: C.gold,       fg: C.charcoal },
  "Transition":       { bg: C.mutedRose,  fg: "#FFFFFF" },
  "Pending":          { bg: C.greySoft,   fg: C.charcoal },
};

const FONT_HEAD = '"Cambria", "Source Serif Pro", Georgia, "Times New Roman", serif';
const FONT_BODY = '"Calibri", "Source Sans Pro", "Segoe UI", system-ui, -apple-system, sans-serif';

const pt = (n) => `${(n * 1.3333).toFixed(2)}px`; // pt → px

// -------------------- Hardcoded visualisation data (Alex Morgan substitutions) --------------------
// Per Revision Build Brief inline SVG reference. Geometry lifted verbatim from reference;
// only the numeric values + Alex polygon + AI dot position substitute Vishnu's values.
const MORGAN_VIS = {
  composition: {
    bri: 62,
    confidence: "VERY LOW",
    layersObservedCount: 1, // TA only
    layers: [
      { key: "SA", name: "Self-Assessment", note: "Diagnostic only", weight: 0,  source: "OBS", score: 65 },
      { key: "MV", name: "Manager Validation",                      weight: 30, source: "INF", score: 60 },
      { key: "TA", name: "Technical Assessment",                    weight: 25, source: "OBS", score: 68 },
      { key: "HOS", name: "Hands-On Simulation",                    weight: 25, source: "INF", score: 55 },
      { key: "TSA", name: "Tools & Skills Adjacency",               weight: 20, source: "INF", score: 62 },
    ],
    reconciliation:
      "BRI Composite: 62/100, Support Developer tier. Confidence VERY LOW (1 of 4 scoring layers observed). Manager Validation, Hands-On Simulation, and Tools and Skills Adjacency are inferred from limited adjacent evidence due to the May 2026 platform admin pause. Re-completion of MV, HOS, and TSA is the named pathway to confirmed tier and confidence. ICARE Floor not triggered.",
  },
  fourLens: {
    // Polygon points: Autonomy (N) -> Vision (E) -> Output (S) -> Working (W). Approved geometry.
    polygon: "0,-62 58,0 0,68 -70,0",
    lenses: [
      { name: "Autonomy",         score: 62, rating: "Adequate",      ratingClass: "adequate",
        body: "Self-Assessment evidence indicates working ownership of assigned tasks within team scope. Cross-team contribution is the named development gap." },
      { name: "Vision",           score: 58, rating: "Thin Evidence", ratingClass: "thin",
        body: "Limited observed evidence on architectural direction or pattern advocacy. This is expected at the Practitioner-IC cadre; development pathway extends this lens." },
      { name: "Working Approach", score: 70, rating: "Strong",        ratingClass: "strong",
        body: "Self-Assessment evidence indicates constructive collaboration within immediate team. Foundation for cross-team contribution gap named in Skill Gap 2." },
      { name: "Output",           score: 68, rating: "Adequate",      ratingClass: "adequate",
        body: "Technical Assessment evidence shows reliable functional delivery. The next-tier capability bar adds code quality practices named in Skill Gap 4." },
    ],
  },
  aiPosture: {
    // Approved dot coordinate: cx=80, cy=140 (centred in DEVELOP quadrant, mirroring Vishnu's SAFE & READY)
    dot: { cx: 80, cy: 140, label: "Alex" },
    quadrant: "DEVELOP",
    quadrantSummary:
      "Quadrant: DEVELOP. Low displacement risk (30) with AI embedding in the Developing band — tool adjacency is in place but AI is not yet integrated into the daily development workflow. Grey dots indicate other cohort positions for reference.",
    displacement: {
      score: 30,
      band: "Low",
      bandClass: "low",
      description:
        "Engineer role with significant judgment-protected components. AI augments code generation but architectural decisions, cross-team contribution, and code quality practices remain durable human work.",
    },
    embedding: {
      band: "Developing",
      bandClass: "developing",
      description:
        "Tool adjacency evident in Self-Assessment but AI is not yet integrated into daily development workflow. Atom Code Certified Practitioner is the named pathway to embedded AI proficiency.",
      tools: ["Atom Code Certified Practitioner", "GitHub Copilot", "Claude Code"],
    },
  },
};

// -------------------- Scoped CSS for the three new visualisations --------------------
// Class names disambiguated per approved lift notes:
//   - bar-fill-muted (was .muted, collided with .muted text colour)
//   - bar-fill-low   (was .low, collided with .band-tag.low)
// Plus .band-tag.developing for Alex's embedding band.
const VIZ_STYLES = `
.viz-section {
  background: #FFFFFF;
  border: 1pt solid #D6D2C7;
  padding: 18px 22px;
  margin-bottom: 14px;
}
.viz-section h2 {
  font-family: "Cambria", "Source Serif Pro", Georgia, serif;
  font-size: 13pt;
  color: #003B28;
  margin: 0 0 14px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
}
.viz-section .sec-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: #00583D;
  color: #FFFFFF;
  font-family: "Cambria", serif;
  font-size: 11pt;
  font-weight: 700;
}
.viz-label {
  font-size: 7.5pt;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #6B7280;
  font-weight: 600;
}
.viz-tiny { font-size: 8pt; }
.viz-muted-text { color: #6B7280; }
.composition-table { width: 100%; border-collapse: collapse; }
.composition-table td {
  padding: 6px 8px;
  font-size: 9pt;
  border-bottom: 0.5pt solid #E8E5DC;
  vertical-align: middle;
}
.composition-table .layer-name { width: 38%; color: #3B4451; }
.composition-table .layer-name strong { color: #1F2933; }
.composition-table .layer-weight { width: 12%; color: #6B7280; font-size: 8pt; }
.composition-table .layer-bar { width: 30%; }
.composition-table .layer-source { width: 10%; text-align: center; }
.composition-table .layer-score {
  width: 10%; text-align: right; font-weight: 700;
  font-variant-numeric: tabular-nums; color: #1F2933;
}
.viz-tag {
  display: inline-block;
  padding: 2px 8px;
  font-size: 7.5pt;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
.viz-tag.obs { background: #DDEBE3; color: #003D29; }
.viz-tag.inf { background: #F4E5C0; color: #1F2933; }
.viz-bar { height: 7px; background: #E8E5DC; position: relative; overflow: hidden; }
.viz-bar-fill { height: 100%; background: #00583D; }
.viz-bar-fill.gold { background: #C28F2C; }
.viz-bar-fill.bar-fill-muted { background: #9CA3AF; }
.viz-bar-fill.bar-fill-low { background: #D6D2C7; }
.lens-item {
  display: grid;
  grid-template-columns: 130px 90px 1fr;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 0.5pt solid #E8E5DC;
  align-items: start;
}
.lens-name {
  font-size: 8.5pt; font-weight: 700; color: #003D29;
  letter-spacing: 0.4px; text-transform: uppercase;
  padding-top: 2px;
}
.lens-rating-tag {
  padding: 3px 8px;
  font-size: 7.5pt;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  text-align: center;
  height: fit-content;
}
.lens-rating-tag.strong { background: #DDEBE3; color: #003D29; }
.lens-rating-tag.adequate { background: #F4E5C0; color: #9B6F1F; }
.lens-rating-tag.thin { background: #DCE0E7; color: #6B7280; }
.lens-text { font-size: 8.5pt; line-height: 1.5; color: #3B4451; }
.band-card {
  background: #FFFFFF;
  border: 1pt solid #D6D2C7;
  padding: 14px;
}
.band-card .viz-label { margin-bottom: 8px; }
.band-tag {
  display: inline-block;
  padding: 4px 12px;
  font-size: 9pt;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 8px;
}
.band-tag.low { background: #DDEBE3; color: #003D29; }
.band-tag.ready { background: #00583D; color: #FFFFFF; }
.band-tag.developing { background: #F4E5C0; color: #9B6F1F; }
.band-detail { font-size: 8.5pt; line-height: 1.5; color: #3B4451; }
.signal-mini-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  margin-top: 8px;
}
.signal-mini {
  background: #F7F5EE;
  padding: 6px 8px;
  text-align: center;
  border: 0.5pt solid #E8E5DC;
}
.signal-mini .viz-label { font-size: 6.5pt; }
.signal-mini .signal-num {
  font-family: "Cambria", serif;
  font-size: 13pt;
  font-weight: 700;
  color: #1F2933;
}
`;

// -------------------- Hardcoded sample (Alex Morgan, verbatim) --------------------
const SAMPLE = {
  identity: {
    name: "Alex Morgan",
    role: "Senior Software Engineer",
    region: "Sample Region North",
    unit: "Sample Business Unit",
    reportsTo: "Jordan Taylor",
    cadre: "Practitioner-IC (SFIA 3-4)",
    skillDomain: "Integrator",
    assessmentVerdict: "DEFER (PLATFORM ISSUE)",
  },
  intelligence: {
    bri: { score: "62 / 100", tier: "Support Developer" },
    confidence: { level: "VERY LOW", note: "1 of 4 scoring layers observed" },
    structureFit: { name: "Sample Value-Stream Function", placement: "At Current Level (provisional)" },
    operationalCategory: { verdict: "Develop" },
    aiPosture: { risk: "Low (Score 30)", readiness: "Developing" },
    flags: ["Apprentice Slot Suitable"],
  },
  verdict:
    "Develop placement provisional at Support Developer tier. Strong engagement signal across observed evidence, with the Technical Assessment layer the only scored layer available. Re-completion of Manager Validation, Hands-On Simulation, and Tools and Skills Adjacency is the named pathway to confirmed tier and confidence.",

  strengths: [
    {
      name: "Consistent Delivery Cadence in Production Code",
      tag: "Evidenced in Technical Assessment",
      body: "Technical Assessment evidence indicates regular shipping of working code across two consecutive cycles, with no notable defect spikes. The delivery rhythm is the foundation that any further development pathway builds on.",
    },
    {
      name: "Active Engagement in Self-Assessment Reflection",
      tag: "Evidenced in Self-Assessment",
      body: "Self-Assessment responses indicate substantive reflection on current role responsibilities, daily tool use, and perceived development areas. This level of self-awareness supports the development conversation that follows.",
    },
    {
      name: "Tool Adjacency Across Modern Development Stack",
      tag: "Evidenced in Self-Assessment",
      body: "Self-Assessment evidence indicates familiarity with version control, continuous integration, and modern development environments. The toolkit foundation is in place; the development pathway extends rather than introduces these capabilities.",
    },
    {
      name: "Constructive Working Approach Within Immediate Team",
      tag: "Evidenced in Self-Assessment",
      body: "Self-Assessment responses indicate constructive collaboration within the immediate team scope. The relationship foundation is the starting point for the cross-team contribution this plan develops.",
    },
  ],

  whereYouAre:
    "You are placed at Support Developer tier, VERY LOW confidence. The Technical Assessment is the only scored layer available for you in this assessment cycle; Manager Validation, Hands-On Simulation, and Tools and Skills Adjacency were not completed due to the May 2026 platform admin pause, not for any reason related to your participation. Per Methodology Section 9.5, tier placement is an interim imputation pending re-assessment, not a defensibility-grade placement. The plan below is calibrated to your Engineer role family, your placement within the Sample Value-Stream Function, and the development pathway from Support Developer toward Emerging Builder.",

  arc:
    "Your consistent delivery cadence is the foundation that the cloud platform credential development extends, because production reliability becomes more valuable as your platform scope broadens. Your active Self-Assessment engagement signals readiness for the structured development conversations that the next 90 days build around. Your tool adjacency means the AI proficiency pathway is a deepening rather than a learning curve; the toolkit is already familiar territory. Your constructive working approach within the immediate team is what makes the cross-team contribution gap a realistic development target, because the relationship pattern that works within one team typically extends to adjacent teams with intention. The development pathway below is not a critique of these strengths; it is an extension of them into the scope, platform depth, and cross-team contribution the function needs from you over the next twelve months.",

  gaps: [
    {
      name: "Cloud Platform Foundational Credential",
      category: "Certification",
      why: "Your current role lacks a named cloud platform credential, which limits the architectural scope of your contribution. The function destination requires platform-credentialed engineers at the cadre level you are working toward.",
      action: "Begin Azure Fundamentals (AZ-900) through Microsoft Learn in the first 30 days. Progress to Azure Developer Associate (AZ-204) by end of Q3.",
      evidence: "AZ-900 credential confirmed by end of Q1. AZ-204 credential confirmed by end of Q3. Application of cloud-specific patterns visible in at least two code reviews during Q3-Q4.",
    },
    {
      name: "Cross-Team Contribution Beyond Immediate Scope",
      category: "Cross-team Influence",
      why: "Your constructive working approach evidence is currently scoped to your immediate team. Emerging Builder placement requires demonstrated contribution to teams adjacent to your own, which extends the same working pattern to a broader audience.",
      action: "Identify one adjacent team for collaboration in Q1. Take named contributor role on one cross-team initiative by end of Q2.",
      evidence: "Named contributor role confirmed in Q2 deliverables. At least one cross-team artefact published with your contribution documented.",
    },
    {
      name: "AI Proficiency in Development Workflow",
      category: "AI Proficiency",
      why: "Your tool adjacency is the foundation, but AI tools are not yet integrated into your daily development workflow per the Self-Assessment evidence. The function destination treats AI proficiency as a durable capability for all engineering roles.",
      action: "Begin Atom Code Certified Practitioner pathway in Q2. Integrate AI coding assistance into daily work by end of Q2.",
      evidence: "Atom Code certification complete by end of Q3. AI tools visible in your daily workflow as evidenced by manager observation and self-reported tool usage.",
    },
    {
      name: "Code Quality Practices Beyond Functional Correctness",
      category: "Technical Skill",
      why: "Technical Assessment evidence shows reliable functional delivery but the next-tier capability bar adds named code quality practices: test coverage, documentation, peer code review. These are the practices that distinguish Support Developer from Emerging Builder.",
      action: "Adopt test-driven development on next two delivery cycles. Document at least one architectural decision per cycle in the team's decision record.",
      evidence: "Test coverage on your delivered code meets team baseline by end of Q2. At least 2 architectural decision records authored by end of Q3.",
    },
    {
      name: "Practical-Layer Evidence Completion",
      category: "Evidence Completion",
      why: "Your placement is Support Developer VERY LOW confidence because three of the four scoring layers (Manager Validation, Hands-On Simulation, Tools and Skills Adjacency) were not completed due to the May 2026 platform admin pause. This is not a capability gap; it is an evidence-base gap that must close before any tier confirmation.",
      action: "Request MV, HOS, and TSA completion through your HR partner in the first 14 days. Schedule a 90-day window for re-completion once the platform pause is resolved.",
      evidence: "All four layers completed with scores logged. Confidence rating updated from VERY LOW to confirmed level. Re-assessment scheduled in Q4.",
    },
  ],

  pathway: {
    phases: [
      {
        label: "First 30 Days",
        items: [
          "Request MV, HOS, and TSA re-completion through HR partner",
          "Enrol in Azure Fundamentals (AZ-900) through Microsoft Learn",
          "Identify one adjacent team for Q2 cross-team collaboration",
          "Establish weekly 1:1 cadence with current line manager",
        ],
      },
      {
        label: "First 90 Days",
        items: [
          "AZ-900 credential complete",
          "First cross-team initiative scoped with named contributor role",
          "Begin Atom Code Certified Practitioner pathway",
          "Test-driven development adopted on current delivery cycle",
        ],
      },
      {
        label: "Six-Month Milestone",
        items: [
          "Azure Developer Associate (AZ-204) study in progress",
          "Atom Code certification complete",
          "Two architectural decision records authored",
          "AI coding tools integrated into daily workflow",
        ],
      },
      {
        label: "Twelve-Month Outcome",
        items: [
          "Tier target: Emerging Builder confirmed with HIGH confidence (all 4 layers re-completed)",
          "AZ-204 credential confirmed",
          "Cross-team contribution sustained across at least two named initiatives",
          "Test coverage and code quality practices meeting team baseline",
        ],
      },
    ],
    modalities: [
      "Subscribe to the Microsoft Learn Azure cloud architecture content for asynchronous study during AZ-900 and AZ-204 windows",
      "Maintain a personal development journal during credential study periods, one cloud architecture pattern per week",
      "Shadow a senior engineer in an adjacent team for one half-day per month",
      "Participate in the team's peer code review rotation as both reviewer and author",
    ],
  },

  credentials: {
    caveat:
      "The Self-Assessment for this candidate did not capture currently held credentials. If you already hold any credential below, the recommendation automatically substitutes the next-tier credential or the most relevant complementary credential. A lightweight credential verification step is requested in Q1 as part of plan kickoff.",
    items: [
      {
        name: "Azure Fundamentals (AZ-900)",
        provider: "Microsoft Learn",
        time: "Approximately 8 weeks of focused preparation",
        investment: "Low",
        eligibility: "Required, eligible for reimbursement under central training budget",
        note: "If you already hold this credential, the recommendation automatically substitutes the next-tier credential.",
      },
      {
        name: "Azure Developer Associate (AZ-204)",
        provider: "Microsoft Learn",
        time: "Approximately 14 weeks of focused preparation",
        investment: "Moderate",
        eligibility: "Required, eligible for reimbursement under central training budget",
        note: "If you already hold this credential, the recommendation automatically substitutes the next-tier credential.",
      },
      {
        name: "Atom Code Certified Practitioner",
        provider: "THCO Atom Code certification programme",
        time: "Approximately 8 weeks of focused preparation",
        investment: "Moderate",
        eligibility: "Required, eligible for reimbursement under central training budget",
        note: "If you already hold this credential, the recommendation automatically substitutes the next-tier credential.",
      },
      {
        name: "Microsoft Fabric Analytics Engineer (DP-600, optional)",
        provider: "Microsoft Learn",
        time: "Approximately 10 weeks of focused preparation",
        investment: "Low",
        eligibility: "Optional",
        note: "If you already hold this credential, the recommendation automatically substitutes the next-tier credential.",
      },
    ],
  },

  aiProficiency:
    "AI proficiency for your role is framed as durable capability with AI coding tools integrated into daily development work. The primary certification vehicle is Atom Code Certified Practitioner, with working familiarity expected across GitHub Copilot, Claude Code, and Cursor. Production application is daily use in code authoring, code review, and test generation. Mentoring scope at your current cadre is informal peer support within your immediate team; formal mentoring scope expands at the Emerging Builder cadre. Disclosure: Atom Code is a THCO product. THCO is the consultancy delivering this assessment. Atom Code is listed as one named certification option among others (GitHub Copilot certification via Microsoft Learn track, vendor-neutral AI proficiency programmes) rather than as the only recommendation.",

  workingApproach:
    "Working approach track: Sharpening. Self-Assessment evidence indicates a constructive working pattern within the immediate team. Development at this cadre focuses on extending the working pattern to the cross-team contribution scope named in Skill Gap 2. The pattern itself does not require remediation; the scope requires broadening.",

  structural: [
    ["Target Function", "Sample Value-Stream Function builder team"],
    ["Current Placement Level", "At Current Level (provisional)"],
    ["Primary Goal Alignment", "Growth (Vertical Value-Stream Functions)"],
    ["Secondary Goal Broadening", "Architecture & AI through AI proficiency pathway"],
    ["Institutional Flags", "Apprentice Slot Suitable (mentee role within Phase 1 programme)"],
    ["Operational Support Handover", "No structural redistribution required at current cadre; revisit at Emerging Builder confirmation"],
  ],

  knowledge: [
    {
      label: "Domain 1",
      name: "Sample Business Unit Application Portfolio",
      vehicle:
        "Structured internal rotation across two business-critical applications in Q1-Q2. Subject-matter expert mentor from the senior engineering bench. Output: one application reference document by end of Q3.",
    },
    {
      label: "Domain 2",
      name: "Cloud Platform Architecture Foundations",
      vehicle:
        "Azure architecture reading list paired with the AZ-900 study window. Quarterly working session with the cloud platform team. Output: one architectural pattern note per study month.",
    },
    {
      label: "Domain 3",
      name: "Test-Driven Development and Code Quality Practices",
      vehicle:
        "Pair-programming sessions with a TDD-experienced senior engineer through Q2. Code review rotation participation. Output: at least 2 architectural decision records by end of Q3.",
    },
  ],

  relationships: [
    ["Senior engineer in your immediate team (named TDD mentor)", "Code quality practices development and TDD pair-programming through Q2", "Weekly pair-programming session"],
    ["Lead of one adjacent team (named in Skill Gap 2)", "Cross-team contribution scope expansion; named initiative collaboration starting Q2", "Monthly working session"],
    ["Cloud platform team representative", "Knowledge depth in cloud platform architecture; AZ-204 study support", "Quarterly working session"],
    ["HR partner", "Plan progression, re-assessment scheduling, evidence-base completion tracking", "Monthly check-in"],
  ],

  portfolio: [
    {
      name: "Application Reference Document (Sample Business Unit Application Portfolio)",
      scope: "One reference document mapping two business-critical applications including architectural overview, integration points, and known constraints",
      audience: "Engineering team, function leadership, future onboarding engineers",
      target: "End of Q3",
    },
    {
      name: "Architectural Decision Records (2+)",
      scope: "Two named architectural decisions documented in the team's decision record format",
      audience: "Engineering team and function leadership",
      target: "Rolling Q2-Q3",
    },
    {
      name: "Cross-Team Initiative Contribution Brief",
      scope: "One brief documenting your contribution to the cross-team initiative named in Skill Gap 2, including scope, outcomes, and lessons learned",
      audience: "Both team leads and function leadership",
      target: "End of Q4",
    },
  ],

  funding: [
    ["Protected Development Time", "3 hours per week of development time, ring-fenced from operational delivery. Calendar-blocked, not nominally allocated. Confirmed by current line manager."],
    ["Credential & Training Funding", "Central training budget funding confirmed for AZ-900, AZ-204, and Atom Code Certified Practitioner. DP-600 funding available under continuing professional development allocation if pursued."],
    ["Manager 1:1 Commitment", "Weekly 1:1 with current line manager. Monthly check-in with HR partner. Quarterly conversation with the adjacent-team lead once cross-team collaboration is established."],
    ["Named Mentor", "Primary mentor: senior engineer in immediate team for TDD pair-programming. Secondary mentor: cloud platform team representative for AZ-204 study support."],
    ["Access Commitments", "Standing invitation to the cloud platform team's monthly architecture review starting Q2. Named participant in the Apprentice Programme Phase 1 cohort. Access to the team's architectural decision record repository for both reading and authoring."],
  ],

  recognition: [
    {
      label: "Institutional Register Memberships",
      headline: "Apprentice Slot Suitable",
      body: "Named in the Apprentice Programme Phase 1 cohort as a mentee role. Recognition that your cadre supports structured mentoring uptake.",
    },
    {
      label: "Named Function Destination",
      headline: "Sample Value-Stream Function builder team",
      body: "Selected for placement at At Current Level (provisional, pending re-assessment). Recognition that the function leadership sees your role family fitting within the new builder team structure.",
    },
    {
      label: "Strategic Initiative Participation",
      headline: "Two named initiatives in 2026",
      items: [
        "Named contributor to the cross-team initiative in Q2.",
        "Named contributor to the AI coding adoption pilot within the engineering team in Q3.",
      ],
    },
    {
      label: "Function Forum Exposure",
      headline: "Two function-level exposures in 2026",
      items: [
        "Cloud platform team monthly architecture review (standing invitation from Q2).",
        "Engineering function quarterly review (attendance once cross-team contribution is established).",
      ],
    },
  ],

  horizon: [
    {
      year: "Year 1",
      label: "Contractual",
      bg: C.forestPale,
      underline: { color: C.forest, style: "solid" },
      body: "Confirmed Support Developer placement transitioning to Emerging Builder by end of Year 1. All four scoring layers re-completed and confidence rating updated. AZ-204 credential held. Cross-team contribution established. Atom Code certification held. Two architectural decision records authored.",
    },
    {
      year: "Year 2",
      label: "Directional",
      bg: C.ivory,
      underline: { color: C.gold, style: "dashed" },
      body: "Confirmed Emerging Builder placement. Named contributor across multiple cross-team initiatives. Mentee role completed; transition toward informal mentoring of newer engineers. Cloud platform contribution scope established. Apprentice Programme Phase 2 participation begins.",
    },
    {
      year: "Year 3",
      label: "Horizon",
      bg: "#FFFFFF",
      underline: { color: C.mutedLight, style: "dotted" },
      body: "Candidate for Core Builder placement at re-assessment. Senior engineer cadre within the Sample Value-Stream Function. Mentoring scope formalised. Named participant in the function's engineering practice forum.",
    },
  ],

  risks: [
    {
      name: "Operational delivery pressure absorbs the protected development time",
      signal: "Weekly 1:1 reports indicate development hours routinely consumed by production work or unplanned escalations. By end of Q1, the protected hours are not appearing in your calendar.",
      mitigation: "Line manager reviews delivery portfolio in Q1 for capacity adjustment. Protected hours are calendar-blocked, not nominally allocated. HR partner involved if pattern persists.",
      escalation: "HR partner > function lead.",
    },
    {
      name: "Platform pause is not resolved in time for MV, HOS, and TSA re-completion in Q2",
      signal: "By end of Q1, the platform admin pause has not been formally resolved or a timeline communicated.",
      mitigation: "HR partner tracks platform resolution as a Q1 priority item. If unresolved by end of Q1, an alternative evidence-completion path is scoped (manager-supervised practical assessment) so re-assessment proceeds without further delay.",
      escalation: "HR partner > THCO operations.",
    },
    {
      name: "Cross-team initiative does not scope or name a contributor role by end of Q2",
      signal: "By end of Q2, no named cross-team initiative has emerged with a contributor role for you.",
      mitigation: "Line manager and adjacent-team lead coordinate to identify a substitute initiative. Cross-team contribution can also be demonstrated through code review support, shared knowledge management contribution, or paired delivery work.",
      escalation: "Line manager > function lead.",
    },
  ],

  stretches: {
    items: [
      {
        name: "Named Contribution to Adjacent Team Initiative",
        allocation: "10 to 15 hours per month from Q2 through Q4 as named contributor on the initiative",
        outcome: "Initiative deliverable contributed to. Output: one cross-team initiative contribution brief by end of Q4 documenting scope, outcomes, and lessons learned.",
        host: "Lead of the adjacent team (named in Skill Gap 2)",
      },
      {
        name: "Cloud Platform Team Knowledge Rotation",
        allocation: "1 day per month from Q2 through Q4 attending cloud platform team architecture review and shadow sessions",
        outcome: "Knowledge depth in cloud platform architecture deepened. Output: one architectural pattern note per attended session.",
        host: "Cloud platform team representative",
      },
    ],
    lighter: [
      "Action learning group with two other Apprentice Programme cohort members: quarterly working sessions on shared development challenges",
      "Job shadow exchange with a peer engineer in another team: one day in their team, one day they spend with you, completed in Q3",
    ],
  },

  milestones: [
    {
      q: "Q1",
      name: "AZ-900 Credential Achievement",
      audience: "Immediate team and line manager",
      form: "Named in team communication; credential added to your professional record",
      trigger: "Completion of Azure Fundamentals certification",
    },
    {
      q: "Q2",
      name: "First Cross-Team Initiative Contribution",
      audience: "Both team leads and function leadership",
      form: "Named contributor on the initiative; contribution acknowledged in initiative wrap-up communication",
      trigger: "Substantive contribution to the cross-team initiative deliverable",
    },
    {
      q: "Q3",
      name: "Atom Code Certified Practitioner Achievement",
      audience: "Engineering team and function leadership",
      form: "Named in function communication; AI champion track positioning established",
      trigger: "Completion of Atom Code certification and demonstrated daily AI tool usage",
    },
    {
      q: "Q4",
      name: "Re-Assessment Conversation Framed as Tier Progression",
      audience: "Function leadership, HR partner, line manager",
      form: "Re-assessment conversation framed as Emerging Builder tier progression, contingent on full evidence-base completion",
      trigger: "All four scoring layers re-completed; portfolio outputs delivered; cross-team contribution established",
    },
  ],

  cohort: {
    identity: "2026 Engineer Apprentice Cohort",
    summary:
      "You are one of approximately 8-12 engineer-cadre candidates in the Apprentice Programme Phase 1, being developed alongside peers across multiple regions. The cohort is convened by HR partners. Cohort members are not visible to each other through the platform; cohort convening is coordinated by the HR partner who holds the named list.",
    cadence: [
      ["Monthly Peer Review", "60-minute structured session: credential progress sharing, code quality practice exchange, cross-team contribution updates. Hosted by HR partner."],
      ["Joint Credential Pursuit", "Coordinated Azure credential pursuit where cohort members are at similar progression points. Shared study materials."],
      ["Paired Stretches", "Where opportunities permit, cohort members can be paired on adjacent-team initiatives for shared cross-team contribution experience."],
      ["Quarterly Cohort Review", "90-minute session with the function engineering lead. Function-level direction discussed; cohort progression reviewed."],
    ],
  },

  measurement: [
    { label: "Re-Assessment Layers (Priority)", body: "MV, HOS, and TSA are the priority layers for re-completion. TA may be re-administered for confidence triangulation. Re-assessment target: Q4 2026." },
    { label: "1:1 Cadence", body: "Weekly with current line manager. Monthly with HR partner. Monthly with adjacent-team lead once cross-team collaboration is established (Q2 onward)." },
    { label: "HR Partner Schedule", body: "Q1: Plan kickoff, credential verification, re-completion arrangement. Q2: Mid-pathway review. Q3: Knowledge depth checkpoint and portfolio review. Q4: Re-assessment and tier progression conversation." },
    { label: "Portfolio Review Cadence", body: "Q3: Application reference document and architectural decision records reviewed by line manager and function leadership. Q4: Cross-team contribution brief delivered; full portfolio reviewed at re-assessment." },
    { label: "Knowledge Depth Checkpoint", body: "Q3: Structured conversation with line manager and subject-matter expert mentors across all three knowledge domains (application portfolio, cloud platform foundations, code quality practices). Validation criteria: ability to apply knowledge in production work, not just describe it." },
    { label: "Relationship Map Review", body: "Q4: Structured review with HR partner of progress on the four named stakeholder relationships. Validation criteria: established cadence, evidence of substantive engagement, named joint outputs where applicable." },
    { label: "Closing Note", body: "This plan is reviewed at every manager 1:1, updated quarterly with the HR partner, and refreshed at re-assessment in Q4. The plan is a living document, not an annual artefact." },
  ],
};

// -------------------- React context to expose live (or fallback) candidate data --------------------
const XrayDataContext = createContext({ data: SAMPLE, vis: MORGAN_VIS });
function useXray() {
  return useContext(XrayDataContext);
}

// -------------------- Reusable building blocks --------------------
const SectionLabel = ({ letter, title, color = C.forest }) => (
  <div data-testid={`section-label-${letter}`} style={{ marginTop: "26px", marginBottom: "12px" }}>
    <div
      style={{
        fontFamily: FONT_HEAD,
        fontWeight: 700,
        fontSize: pt(11),
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        color: C.forestDark,
        borderBottom: `2px solid ${color}`,
        paddingBottom: "4px",
      }}
    >
      Section {letter} · {title}
    </div>
  </div>
);

const Label = ({ children, color = C.forest }) => (
  <div
    style={{
      fontFamily: FONT_BODY,
      fontWeight: 700,
      fontSize: pt(8),
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      color,
      marginBottom: "3px",
    }}
  >
    {children}
  </div>
);

const Para = ({ children, size = 9, color = C.charcoal }) => (
  <p
    style={{
      fontFamily: FONT_BODY,
      fontSize: pt(size),
      color,
      lineHeight: 1.55,
      margin: 0,
    }}
  >
    {children}
  </p>
);

const PanelCard = ({ children, leftBorder, bg = "#FFFFFF", style = {} }) => (
  <div
    style={{
      backgroundColor: bg,
      border: `0.7pt solid ${C.line}`,
      borderLeft: leftBorder ? `3pt solid ${leftBorder}` : `0.7pt solid ${C.line}`,
      padding: "14px 16px",
      ...style,
    }}
  >
    {children}
  </div>
);

const NumberBubble = ({ n, color }) => (
  <div
    style={{
      width: "22px",
      height: "22px",
      borderRadius: "50%",
      backgroundColor: color,
      color: "#FFFFFF",
      fontFamily: FONT_HEAD,
      fontWeight: 700,
      fontSize: pt(9.5),
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "0 0 22px",
    }}
  >
    {n}
  </div>
);

const Pill = ({ children, fg = C.charcoalSoft, bg = "#FFFFFF", border = C.line }) => (
  <span
    style={{
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: "999px",
      border: `0.7pt solid ${border}`,
      backgroundColor: bg,
      color: fg,
      fontFamily: FONT_BODY,
      fontSize: pt(7.5),
      letterSpacing: "0.3px",
      textTransform: "uppercase",
      fontWeight: 700,
    }}
  >
    {children}
  </span>
);

// -------------------- Page --------------------
export default function XrayTemplatePreview() {
  const navigate = useNavigate();
  const { candidateId } = useParams();
  const [accessState, setAccessState] = useState("checking"); // checking | allowed | denied
  const [liveState, setLiveState] = useState({ status: "idle", data: null, error: null });
  // status: idle (no candidateId - use Alex fallback) | loading | loaded | not_found | error
  const [cohort, setCohort] = useState([]); // ordered list of {candidate_id, name} for prev/next navigation

  // Route-aware auth: when this page is rendered under /executive/... Neil's session
  // token lives in localStorage as `executiveToken`, while /admin/... uses `adminToken`.
  const onExecRoute = window.location.pathname.startsWith("/executive");
  const authLoginPath = onExecRoute ? "/executive/login" : "/admin/login";
  const authFallbackPath = onExecRoute ? "/executive" : "/admin";
  const readAuthToken = () => (
    onExecRoute
      ? (localStorage.getItem("executiveToken") || localStorage.getItem("adminToken") || localStorage.getItem("authToken"))
      : (localStorage.getItem("adminToken") || localStorage.getItem("authToken"))
  );
  const clearAuthToken = () => {
    if (onExecRoute) localStorage.removeItem("executiveToken");
    else localStorage.removeItem("adminToken");
  };

  useEffect(() => {
    let cancelled = false;
    async function check() {
      const token = readAuthToken();
      if (!token) {
        if (!cancelled) navigate(authLoginPath, { replace: true });
        return;
      }
      try {
        const res = await axios.get(`${API}/api/admin/xray-preview-access`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res.data && res.data.allowed === true) {
          setAccessState("allowed");
        } else if (!cancelled) {
          setAccessState("denied");
          navigate(authFallbackPath, { replace: true });
        }
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 401) {
          clearAuthToken();
          navigate(authLoginPath, { replace: true });
        } else {
          setAccessState("denied");
          navigate(authFallbackPath, { replace: true });
        }
      }
    }
    check();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // Fetch the candidate's X-Ray record once access is allowed and a candidateId is present
  useEffect(() => {
    if (accessState !== "allowed") return;
    if (!candidateId) {
      setLiveState({ status: "idle", data: null, error: null });
      return;
    }
    let cancelled = false;
    setLiveState({ status: "loading", data: null, error: null });
    const token = readAuthToken();
    axios.get(`${API}/api/admin/xray/${candidateId}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      if (!cancelled) setLiveState({ status: "loaded", data: res.data, error: null });
    }).catch(e => {
      if (cancelled) return;
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 404) {
        setLiveState({ status: "not_found", data: null, error: detail || { error: "not_found", candidate_id: candidateId } });
      } else {
        setLiveState({ status: "error", data: null, error: detail || { error: "fetch_failed", message: e.message } });
      }
    });
    return () => { cancelled = true; };
  }, [accessState, candidateId]);

  // Fetch the cohort id/name list once (used by the side prev/next chevrons)
  useEffect(() => {
    if (accessState !== "allowed") return;
    let cancelled = false;
    const token = readAuthToken();
    axios.get(`${API}/api/admin/xray-list`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (cancelled) return;
        // Exclude AVP+/VP+/Chief/CIO/CISO/CTO and SBBL-held candidates from cohort prev/next navigation
        // (matches the AdminXrayCohort list exclusions). Records remain accessible via direct URL.
        const items = (res.data?.items || [])
          .filter((r) => !isExecutiveRole(r.role) && !isSbblHeld(r))
          .map((r) => ({ candidate_id: r.candidate_id, name: r.name }));
        setCohort(items);
      })
      .catch(() => { /* navigator simply won't show if list fails */ });
    return () => { cancelled = true; };
  }, [accessState]);

  if (accessState !== "allowed") {
    return (
      <div
        data-testid="xray-preview-loading"
        style={{
          fontFamily: FONT_BODY,
          color: C.charcoalSoft,
          padding: "40px",
          backgroundColor: "#FFFFFF",
          minHeight: "100vh",
        }}
      >
        Verifying access…
      </div>
    );
  }

  // Loading state — candidate fetch in flight
  if (liveState.status === "loading") {
    return (
      <div
        data-testid="xray-preview-loading-candidate"
        style={{ fontFamily: FONT_BODY, color: C.charcoalSoft, padding: "40px", backgroundColor: "#FFFFFF", minHeight: "100vh" }}
      >
        Loading X-Ray for candidate {candidateId}…
      </div>
    );
  }

  // Not-found state — render empty state with Generate button
  if (liveState.status === "not_found") {
    return <XrayNotFoundEmptyState candidateId={candidateId} error={liveState.error} />;
  }

  // Fetch error state
  if (liveState.status === "error") {
    return (
      <div
        data-testid="xray-preview-error"
        style={{ fontFamily: FONT_BODY, color: C.mutedRose, padding: "40px", backgroundColor: "#FFFFFF", minHeight: "100vh" }}
      >
        Error loading X-Ray: {liveState.error?.message || liveState.error?.error || "Unknown error"}
      </div>
    );
  }

  // Executive safety net: if the candidate is AVP+/VP+/Chief/CIO/CISO/CTO and any forbidden
  // tier/verdict/disposition snuck into the DB body, scrub it client-side and flag the page.
  const rawLive = liveState.status === "loaded" && liveState.data ? liveState.data : null;

  // SBBL hold: if a direct URL deep-links to a held Sagicor Bank Barbados candidate, show
  // the hold notice instead of the X-Ray. The cohort listing and prev/next navigator
  // already exclude these candidates upstream; this is the third-layer enforcement.
  if (rawLive && isSbblHeld(rawLive)) {
    return <SbblHoldNotice candidateName={rawLive?.identity?.name} navigate={navigate} />;
  }

  const execNet = applyExecutiveSafetyNet(rawLive);
  const renderSample = execNet.body || rawLive || SAMPLE;
  const renderVis = liveState.status === "loaded" && liveState.data
    ? {
        composition: liveState.data.score_composition || MORGAN_VIS.composition,
        fourLens: liveState.data.four_lens_audit || MORGAN_VIS.fourLens,
        aiPosture: liveState.data.ai_posture_render || MORGAN_VIS.aiPosture,
      }
    : MORGAN_VIS;
  return (
    <XrayDataContext.Provider value={{ data: renderSample, vis: renderVis }}>
    <div
      data-testid="xray-template-preview-page"
      style={{
        fontFamily: FONT_BODY,
        backgroundColor: "#FFFFFF",
        color: C.charcoal,
        minHeight: "100vh",
        padding: "24px 0 80px",
      }}
    >
      {liveState.status === "loaded" && (
        <XrayWiringBanner liveData={liveState.data} />
      )}
      {execNet.protected && (
        <ExecutiveProtectedBanner scrubbed={execNet.scrubbed} />
      )}
      <BackToCohortButton navigate={navigate} />
      <CohortNavigator cohort={cohort} candidateId={candidateId} navigate={navigate} />
      <style>{VIZ_STYLES}</style>
      <div style={{ maxWidth: "8.5in", margin: "0 auto", padding: "0 24px" }}>
        {/* Block 1 — Candidate Header Strip */}
        <HeaderStrip />

        {/* Legend Panel — explains tier + cadre taxonomies */}
        <LegendPanel />

        {/* Block 2 — Assessment Intelligence Panel */}
        <IntelligencePanel />
        <VerdictPanel />

        {/* === Phase 1e: Strategic Assessment === */}
        <StrategicAssessmentDiagram data={renderSample} />

        {/* === Required Visualisations (Methodology v4.0) === */}
        <ScoreCompositionWheel />
        <FourLensRadar />
        <AIPostureMatrix />

        {/* === Phase 1e: Technical Competency Detail Table === */}
        <TechnicalCompetencyTable data={renderSample} />

        {/* === Phase 1f: Function-Fit Recommendation (non-exec only; AVP+ candidates omit this panel entirely) === */}
        {!isExecutiveRole(renderSample?.identity?.role) && (
          <RecommendationLayer data={renderSample} />
        )}

        {/* Block 3 — Personal Development Plan banner */}
        <PDPBanner />

        {/* Sections A–T */}
        <SectionA />
        <SectionB />
        <SectionC />
        <SectionD />
        <SectionE />
        <BuilderPathDiagram data={renderSample} />
        <SectionF />
        <InvestmentTrainingRoadmap data={renderSample} />
        <SectionG />
        <SectionH />
        <SectionI />
        <SectionJ />
        <SectionK />
        <SectionL />
        <SectionM />
        <SectionN />
        <SectionO />
        <SectionP />
        <SectionQ />
        <SectionR />
        <SectionS />
        <SectionT />

        {/* Footer meta */}
        <div
          style={{
            marginTop: "40px",
            paddingTop: "12px",
            borderTop: `0.7pt solid ${C.lineSoft}`,
            fontFamily: FONT_BODY,
            fontSize: pt(7),
            color: C.muted,
            textAlign: "center",
          }}
        >
          Candidate X-Ray Template Preview · Admin-only · Sagicor Technology Capability Assessment · Methodology v4.0
        </div>
      </div>
    </div>
    </XrayDataContext.Provider>
  );
}

// -------------------- Cohort prev/next side navigator --------------------
function CohortNavigator({ cohort, candidateId, navigate }) {
  // Hide entirely if we don't have a candidateId (Alex Morgan fallback) or no cohort yet
  if (!candidateId || !cohort || cohort.length === 0) return null;
  const idx = cohort.findIndex((c) => c.candidate_id === candidateId);
  if (idx === -1) return null; // candidate not in list; suppress arrows

  const total = cohort.length;
  // Wrap-around navigation so users can flip indefinitely through the cohort
  const prev = cohort[(idx - 1 + total) % total];
  const next = cohort[(idx + 1) % total];

  const baseBtn = {
    position: "fixed",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 1000,
    width: 48,
    height: 80,
    background: "linear-gradient(135deg, #00B5EF 0%, #82C341 100%)",
    color: "#FFFFFF",
    border: 0,
    cursor: "pointer",
    fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    fontSize: 26,
    fontWeight: 600,
    boxShadow: "0 2px 8px rgba(0,153,204,0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 150ms ease",
  };
  const isExecRoute = window.location.pathname.startsWith("/executive");
  const previewBase = isExecRoute ? "/executive/xray-template-preview" : "/admin/xray-template-preview";
  const hoverOn = (e) => {
    e.currentTarget.style.background = "linear-gradient(135deg, #0099CC 0%, #5F9A2A 100%)";
    e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,153,204,0.35)";
    e.currentTarget.style.width = "54px";
  };
  const hoverOff = (e) => {
    e.currentTarget.style.background = "linear-gradient(135deg, #00B5EF 0%, #82C341 100%)";
    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,153,204,0.25)";
    e.currentTarget.style.width = "48px";
  };
  return (
    <>
      <button
        data-testid="cohort-nav-prev"
        title={`Previous — ${prev.name}`}
        aria-label={`Previous candidate: ${prev.name}`}
        onClick={() => navigate(`${previewBase}/${prev.candidate_id}`)}
        onMouseEnter={hoverOn}
        onMouseLeave={hoverOff}
        style={{ ...baseBtn, left: 0, borderTopRightRadius: 12, borderBottomRightRadius: 12 }}
      >
        <span aria-hidden style={{ marginLeft: -2 }}>{"\u2039"}</span>
      </button>

      <div
        data-testid="cohort-nav-counter"
        style={{
          position: "fixed",
          bottom: 16,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1000,
          padding: "6px 14px",
          background: "rgba(31,41,51,0.92)",
          color: "#FFFFFF",
          borderRadius: 999,
          fontSize: 11,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontWeight: 600,
          letterSpacing: "0.04em",
          boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
          pointerEvents: "none",
        }}
      >
        {idx + 1} / {total}
      </div>

      <button
        data-testid="cohort-nav-next"
        title={`Next — ${next.name}`}
        aria-label={`Next candidate: ${next.name}`}
        onClick={() => navigate(`/admin/xray-template-preview/${next.candidate_id}`)}
        onMouseEnter={hoverOn}
        onMouseLeave={hoverOff}
        style={{ ...baseBtn, right: 0, borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }}
      >
        <span aria-hidden style={{ marginRight: -2 }}>{"\u203A"}</span>
      </button>
    </>
  );
}

// -------------------- SBBL hold notice (full-page) --------------------
function SbblHoldNotice({ candidateName, navigate }) {
  return (
    <div
      data-testid="sbbl-hold-notice"
      style={{
        minHeight: "100vh",
        background: "#FFFFFF",
        padding: "80px 24px",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          maxWidth: 640,
          width: "100%",
          background: "#FBF6E9",
          border: "1px solid #C28F2C",
          borderLeft: "4px solid #C28F2C",
          borderRadius: 8,
          padding: "32px 36px",
          color: "#5C4514",
          fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
          <span aria-hidden style={{ fontSize: 28, lineHeight: 1 }}>{"\u26E8"}</span>
          <div style={{ fontSize: 13, letterSpacing: 1.5, textTransform: "uppercase", color: "#9A6B1F", fontWeight: 700 }}>
            On hold
          </div>
        </div>
        <h1 style={{ fontFamily: 'Cambria, Georgia, serif', fontSize: 24, color: "#5C4514", margin: "0 0 14px", lineHeight: 1.25 }}>
          Sagicor Bank Barbados report not yet released
        </h1>
        <p style={{ fontSize: 14, lineHeight: 1.6, margin: "0 0 14px" }}>
          {candidateName ? <strong style={{ color: "#3F2E0E" }}>{candidateName}</strong> : "This candidate"} is part of the Sagicor Bank Barbados (SBBL) population. Per Sagicor leadership directive, X-Ray reports for the Sagicor Bank cohort are currently on hold and will not surface in the platform until the SBBL reporting workstream is approved for release.
        </p>
        <p style={{ fontSize: 13, lineHeight: 1.6, margin: "0 0 22px", color: "#5C4514" }}>
          The underlying assessment record remains intact in the database; only the rendering is held.
        </p>
        <button
          data-testid="sbbl-hold-back-btn"
          onClick={() => navigate("/admin/xray-cohort")}
          style={{
            padding: "10px 20px",
            background: "linear-gradient(135deg, #00B5EF 0%, #82C341 100%)",
            color: "#FFFFFF",
            border: 0,
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 2px 6px rgba(0,153,204,0.22)",
          }}
        >
          ← Back to Cohort
        </button>
      </div>
    </div>
  );
}

// -------------------- Executive-protected banner --------------------
function ExecutiveProtectedBanner({ scrubbed }) {
  // Only show the visible banner when we actually had to scrub a forbidden value.
  // The protected-class status itself is silent — it's only worth a UI note if
  // legacy data accidentally surfaces a restructure/exit value.
  if (!scrubbed) return null;
  return (
    <div
      data-testid="exec-protected-banner"
      style={{
        maxWidth: "8.5in",
        margin: "0 auto 8px",
        padding: "10px 16px",
        background: "#FBF6E9",
        border: "1px solid #C28F2C",
        borderLeft: "4px solid #C28F2C",
        borderRadius: 6,
        color: "#5C4514",
        fontSize: 12,
        lineHeight: 1.4,
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>{"\u26E8"}</span>
      <span>
        <strong>Protected — no restructure recommendation.</strong>{" "}
        This candidate holds a senior leadership role (AVP / VP / SVP / Chief / CIO / CISO / CTO).
        Per Sagicor leadership directive, any prior data indicating restructuring or exit has been
        suppressed; the X-Ray renders as a development plan only.
      </span>
    </div>
  );
}

// -------------------- Back to Cohort floating button --------------------
function BackToCohortButton({ navigate }) {
  const handleBack = () => {
    const isExecRoute = window.location.pathname.startsWith("/executive");
    const cohortPath = isExecRoute ? "/executive/xray-cohort" : "/admin/xray-cohort";
    // Prefer browser history (preserves filters on the cohort page if user came from there)
    if (window.history.length > 1 && document.referrer && document.referrer.includes(window.location.host)) {
      navigate(-1);
    } else {
      navigate(cohortPath);
    }
  };
  return (
    <button
      data-testid="xray-back-to-cohort-btn"
      onClick={handleBack}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "linear-gradient(135deg, #0099CC 0%, #5F9A2A 100%)";
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,153,204,0.30)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "linear-gradient(135deg, #00B5EF 0%, #82C341 100%)";
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,153,204,0.22)";
      }}
      style={{
        position: "fixed",
        top: 20,
        left: 20,
        zIndex: 1000,
        padding: "10px 18px 10px 14px",
        background: "linear-gradient(135deg, #00B5EF 0%, #82C341 100%)",
        color: "#FFFFFF",
        border: 0,
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: "0.02em",
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        cursor: "pointer",
        boxShadow: "0 2px 6px rgba(0,153,204,0.22)",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        transition: "all 150ms ease",
      }}
      aria-label="Back to X-Ray Cohort"
      title="Back to X-Ray Cohort"
    >
      <span style={{ fontSize: 16, lineHeight: 1 }}>←</span>
      Back to Cohort
    </button>
  );
}

// -------------------- X-Ray wiring banner (audit footer: schema + methodology + generated_at) --------------------
function XrayWiringBanner({ liveData }) {
  const cid = liveData?.candidate_id || "";
  const name = liveData?.identity?.name || liveData?.candidate_name || cid;
  const schema = liveData?.schema_version || "?";
  const methodology = liveData?.methodology_version || "?";
  const generatedAt = liveData?.generated_at ? new Date(liveData.generated_at).toISOString().replace("T", " ").slice(0, 19) + " UTC" : "—";
  return (
    <div
      data-testid="xray-wiring-banner"
      style={{
        background: "#F1F8E4",
        borderTop: "3px solid",
        borderImage: "linear-gradient(90deg, #00B5EF 0%, #82C341 100%) 1",
        borderBottom: "1px solid #82C341",
        padding: "10px 24px",
        fontFamily: FONT_BODY,
        fontSize: pt(8),
        color: C.charcoal,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "16px",
      }}
    >
      <div>
        <strong style={{ color: "#5F9A2A" }}>X-RAY</strong>
        {" · "}
        <span>{name}</span>
        {" · "}
        <span style={{ fontFamily: "monospace", fontSize: pt(7), color: C.charcoalSoft }}>{cid}</span>
      </div>
      <div style={{ fontSize: pt(7), color: C.charcoalSoft }}>
        schema v{schema} · methodology v{methodology} · generated {generatedAt}
      </div>
    </div>
  );
}

// -------------------- X-Ray Not Found Empty State --------------------
function XrayNotFoundEmptyState({ candidateId, error }) {
  const candName = error?.candidate_name || candidateId;
  return (
    <div
      data-testid="xray-not-found-empty-state"
      style={{
        fontFamily: FONT_BODY,
        color: C.charcoal,
        padding: "60px 40px",
        backgroundColor: "#FFFFFF",
        minHeight: "100vh",
        maxWidth: "640px",
        margin: "0 auto",
      }}
    >
      <h2 style={{ fontFamily: FONT_HEAD, color: C.forest, marginBottom: "12px" }}>
        No X-Ray generated yet
      </h2>
      <p style={{ color: C.charcoalSoft, marginBottom: "24px" }}>
        Candidate <strong>{candName}</strong> does not yet have a generated X-Ray.
      </p>
      <p style={{ fontSize: pt(8), color: C.muted, fontFamily: "monospace" }}>
        candidate_id: {candidateId}
      </p>
      <p style={{ fontSize: pt(8), color: C.muted, marginTop: "24px" }}>
        {error?.suggestion || "POST /api/admin/xray/generate to create one (Sub-Phase B)."}
      </p>
    </div>
  );
}

// -------------------- Block 1 — Header Strip --------------------
function HeaderStrip() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  const id = SAMPLE.identity;
  // Tier + confidence resolved from the intelligence panel (single source of truth).
  const tier = SAMPLE.intelligence.bri.tier;
  const confidence = SAMPLE.intelligence.confidence.level;
  const badge = TIER_BADGE[tier] || { bg: C.charcoalSoft, fg: "#FFFFFF" };
  // 8% feather between bands per approved spec (transition zones 21-29, 46-54, 71-79).
  const bandGradient = `linear-gradient(
    to right,
    ${C.forestDark} 0%,
    ${C.forestDark} 21%,
    ${C.forest} 29%,
    ${C.forest} 46%,
    #00674A 54%,
    #00674A 71%,
    ${C.forest} 79%,
    ${C.forest} 100%
  )`;
  return (
    <div
      data-testid="header-strip"
      style={{
        position: "relative",
        background: bandGradient,
        color: "#FFFFFF",
        padding: "20px 26px",
        borderTop: "3px solid transparent",
        backgroundImage: `linear-gradient(90deg, #00B5EF 0%, #82C341 100%), ${bandGradient}`,
        backgroundOrigin: "border-box",
        backgroundClip: "border-box, padding-box",
      }}
    >
      {/* Left content stack reserves space on the right for the absolute-positioned badge */}
      <div style={{ paddingRight: "160px" }}>
        <div
          style={{
            fontFamily: FONT_BODY,
            fontSize: pt(7.5),
            letterSpacing: "1.5px",
            textTransform: "uppercase",
            opacity: 0.85,
            marginBottom: "6px",
          }}
        >
          Candidate X-Ray · Methodology v4.0
        </div>
        <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(18), lineHeight: 1.1 }}>
          {id.name}
        </div>
        {/* Headline identity line: [Cadre] · [Tier] */}
        <div
          style={{
            marginTop: "6px",
            fontFamily: FONT_HEAD,
            fontWeight: 400,
            fontSize: pt(12),
            letterSpacing: "0.5px",
            color: "rgba(255,255,255,0.95)",
            lineHeight: 1.2,
          }}
        >
          {id.cadre} · {tier}
        </div>
        <div
          style={{
            marginTop: "8px",
            fontFamily: FONT_BODY,
            fontSize: pt(9),
            color: "rgba(255,255,255,0.92)",
            lineHeight: 1.45,
          }}
        >
          {id.role} · {id.region} · {id.unit} · Reports to {id.reportsTo}
        </div>
      </div>

      {/* Tier + Confidence badge — top-right, vertically centred against the top-left label */}
      <div
        data-testid="header-tier-badge"
        style={{
          position: "absolute",
          top: "20px",
          right: "26px",
          width: "140px",
          padding: "6px 12px",
          borderRadius: "4px",
          border: "0.5pt solid rgba(255,255,255,0.15)",
          backgroundColor: badge.bg,
          color: badge.fg,
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontFamily: FONT_HEAD,
            fontWeight: 700,
            fontSize: pt(9),
            letterSpacing: "1px",
            textTransform: "uppercase",
            lineHeight: 1.2,
          }}
        >
          {tier}
        </div>
        <div
          style={{
            marginTop: "2px",
            fontFamily: FONT_BODY,
            fontWeight: 400,
            fontSize: pt(7),
            letterSpacing: "0.8px",
            textTransform: "uppercase",
            opacity: 0.85,
            lineHeight: 1.2,
          }}
        >
          {confidence} Confidence
        </div>
      </div>

      {/* Skill Domain pill — under the tier badge */}
      <div
        data-testid="header-skill-domain-pill"
        style={{
          position: "absolute",
          top: "98px",
          right: "26px",
          width: "140px",
          padding: "4px 10px",
          borderRadius: "4px",
          border: "0.5pt solid rgba(255,255,255,0.25)",
          backgroundColor: C.ivory,
          color: C.charcoal,
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontFamily: FONT_BODY,
            fontWeight: 700,
            fontSize: pt(7),
            letterSpacing: "1px",
            textTransform: "uppercase",
            lineHeight: 1.2,
            opacity: 0.7,
          }}
        >
          Skill Domain
        </div>
        <div
          style={{
            marginTop: "1px",
            fontFamily: FONT_HEAD,
            fontWeight: 700,
            fontSize: pt(9),
            letterSpacing: "0.6px",
            textTransform: "uppercase",
            lineHeight: 1.2,
          }}
        >
          {id.skillDomain}
        </div>
      </div>
    </div>
  );
}

// -------------------- Legend Panel — Tier + Cadre taxonomies --------------------
const LEGEND_TIERS = [
  { name: "Core Builder",             swatch: C.forest,      def: "Confirmed senior capability across all four scoring layers. Operates as a recognised owner within their function." },
  { name: "Emerging Builder",         swatch: C.forestMid2,  def: "Builder-track capability with one or more layers still developing. Named pathway to Core Builder within 12 months." },
  { name: "Support Developer",        swatch: C.gold,        def: "Independent contributor within scoped work. Active development pathway toward Emerging Builder." },
  { name: "Support Developer (PRR)",  swatch: C.amberDarker, def: "Mixed evidence across scoring layers. Tier provisionally held pending additional evidence collection." },
  { name: "Restructuring Candidate",  swatch: C.mutedRose,   def: "Capability profile does not match current role at confidence required. Structured HR conversation indicated." },
  { name: "No Data",                  swatch: C.greySoft,    def: "Insufficient evidence for tier placement. Scoring layers awaiting completion." },
];

const LEGEND_CADRES = [
  { name: "Apprentice (SFIA 1-2)",       def: "Entry-level role. Working under direct supervision in structured tasks. Follow / Assist level." },
  { name: "Practitioner-IC (SFIA 3-4)",  def: "Independent contributor applying established processes. Apply / Enable level. Service Desk Admin past trainee, Programmer Analyst, junior developer." },
  { name: "Senior IC (SFIA 4-5)",        def: "Independent contributor with substantial autonomy and complex technical ownership. Enable / Ensure-Advise level. Senior Systems Specialist, Tech Lead, Senior Developer." },
  { name: "Architect-track (SFIA 5-6)",  def: "Architectural authority across multiple systems. Ensure-Advise / Initiate-Influence level. Architect, Senior Architect, Manager." },
  { name: "Executive-track (SFIA 6)",    def: "Technology strategy and direction. Initiate-Influence level. AVP, VP, SVP and above." },
];

function LegendPanel() {
  const colHeaderStyle = {
    fontFamily: FONT_BODY,
    fontSize: pt(8.5),
    fontWeight: 700,
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    color: C.goldDark,
    marginBottom: "10px",
  };
  const itemRowStyle = { display: "flex", gap: "8px", marginBottom: "8px", alignItems: "flex-start" };
  const labelStyle = { fontFamily: FONT_BODY, fontWeight: 700, fontSize: pt(9), color: C.charcoal };
  const defStyle = { fontFamily: FONT_BODY, fontSize: pt(8.5), color: "#3B4451", lineHeight: 1.5 };
  return (
    <div
      data-testid="legend-panel"
      style={{
        marginTop: "12px",
        backgroundColor: C.forestPale,
        border: "0.5pt solid #D6D2C7",
        padding: "14px 16px",
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "32px",
      }}
    >
      {/* Tier column */}
      <div data-testid="legend-tier-col">
        <div style={colHeaderStyle}>Tiers · Capability Placement</div>
        {LEGEND_TIERS.map((t) => (
          <div key={t.name} style={itemRowStyle}>
            <div
              aria-hidden="true"
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "2px",
                backgroundColor: t.swatch,
                flex: "0 0 12px",
                marginTop: "3px",
                border: "0.5pt solid rgba(0,0,0,0.12)",
              }}
            />
            <div style={defStyle}>
              <span style={labelStyle}>{t.name}</span> — {t.def}
            </div>
          </div>
        ))}
      </div>
      {/* Cadre column */}
      <div data-testid="legend-cadre-col">
        <div style={colHeaderStyle}>Cadres · Role Level</div>
        {LEGEND_CADRES.map((c) => (
          <div key={c.name} style={{ marginBottom: "8px" }}>
            <div style={defStyle}>
              <span style={labelStyle}>{c.name}</span> — {c.def}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// -------------------- Block 2 — Intelligence Panel --------------------
function IntelCard({ accent, title, value, sub, items }) {
  return (
    <div
      style={{
        backgroundColor: "#FFFFFF",
        border: `0.7pt solid ${C.line}`,
        borderLeft: `3pt solid ${accent}`,
        padding: "12px 14px",
      }}
    >
      <Label color={accent === C.gold ? C.goldDark : C.forest}>{title}</Label>
      {value ? (
        <div
          style={{
            fontFamily: FONT_HEAD,
            fontWeight: 700,
            fontSize: pt(13),
            color: C.charcoal,
            marginTop: "2px",
          }}
        >
          {value}
        </div>
      ) : null}
      {sub ? (
        <div style={{ fontFamily: FONT_BODY, fontSize: pt(8.5), color: C.charcoalSoft, marginTop: "4px" }}>
          {sub}
        </div>
      ) : null}
      {items ? (
        <ul style={{ margin: "6px 0 0 14px", padding: 0, fontFamily: FONT_BODY, fontSize: pt(9), color: C.charcoal }}>
          {items.map((it, i) => (
            <li key={i} style={{ marginBottom: "2px" }}>{it}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function IntelligencePanel() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  const i = SAMPLE.intelligence;
  return (
    <div data-testid="intelligence-panel" style={{ marginTop: "20px" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
        <IntelCard accent={C.forest} title="BRI Score" value={i.bri.score} sub={`Tier: ${i.bri.tier}`} />
        <IntelCard accent={C.forest} title="Confidence" value={i.confidence.level} sub={i.confidence.note} />
        <IntelCard accent={C.gold} title="Structure Fit" value={i.structureFit.name} sub={i.structureFit.placement} />
        <IntelCard accent={C.gold} title="Operational Category" value={i.operationalCategory.verdict} sub="Verdict" />
        <IntelCard
          accent={C.forest}
          title="AI Posture"
          value={null}
          sub={null}
          items={[`Displacement Risk: ${i.aiPosture.risk}`, `Embedding Readiness: ${i.aiPosture.readiness}`]}
        />
        <IntelCard accent={C.gold} title="Institutional Flags" value={null} sub={null} items={i.flags} />
      </div>
    </div>
  );
}

function VerdictPanel() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  const verdictKey = SAMPLE.identity.assessmentVerdict;
  const verdictStyle = VERDICT_PILL[verdictKey] || { bg: C.greySoft, fg: C.charcoal };
  return (
    <div data-testid="verdict-panel" style={{ marginTop: "14px" }}>
      <PanelCard leftBorder={C.forestDark} bg={C.forestPale}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "14px", flexWrap: "wrap" }}>
          <Label color={C.forestDark}>Verdict</Label>
          <div
            data-testid="assessment-verdict-pill"
            style={{
              backgroundColor: verdictStyle.bg,
              color: verdictStyle.fg,
              padding: "4px 12px",
              borderRadius: "4px",
              border: "0.5pt solid rgba(0,0,0,0.1)",
              fontFamily: FONT_BODY,
              fontWeight: 700,
              fontSize: pt(8),
              letterSpacing: "0.8px",
              textTransform: "uppercase",
              lineHeight: 1.2,
            }}
          >
            Assessment Verdict · {verdictKey}
          </div>
        </div>
        <div style={{ marginTop: "8px" }}>
          <Para size={9}>{SAMPLE.verdict}</Para>
        </div>
      </PanelCard>
    </div>
  );
}

// -------------------- Block 3 — PDP banner --------------------
function PDPBanner() {
  return (
    <div data-testid="pdp-banner" style={{ marginTop: "26px", backgroundColor: C.forest, color: "#FFFFFF", padding: "14px 20px" }}>
      <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(13) }}>
        Personal Development Plan · Methodology §21
      </div>
      <div style={{ marginTop: "6px", fontFamily: FONT_BODY, fontStyle: "italic", fontSize: pt(8.5), color: "rgba(255,255,255,0.92)" }}>
        This plan is a living document. It is reviewed at each manager 1:1, updated quarterly with your HR partner, and refreshed at re-assessment. You work this plan; you do not just read it.
      </div>
    </div>
  );
}

// -------------------- Sections --------------------
function SectionA() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  return (
    <div data-testid="section-a">
      <SectionLabel letter="A" title="Strengths You Bring" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
        {SAMPLE.strengths.map((s, i) => (
          <PanelCard key={i} leftBorder={C.forest} bg={C.forestPale}>
            <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
              <NumberBubble n={i + 1} color={C.forest} />
              <div>
                <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(10.5), color: C.forestDark }}>
                  {s.name}
                </div>
                <div style={{ marginTop: "4px" }}>
                  <Pill fg={C.forest} bg="#FFFFFF" border={C.forestMid}>{s.tag}</Pill>
                </div>
                <div style={{ marginTop: "8px" }}>
                  <Para size={9}>{s.body}</Para>
                </div>
              </div>
            </div>
          </PanelCard>
        ))}
      </div>
    </div>
  );
}

function SectionB() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  return (
    <div data-testid="section-b">
      <SectionLabel letter="B" title="Where You Are Now" />
      <PanelCard leftBorder={C.forest} bg={C.forestPale}>
        <Para size={9}>{SAMPLE.whereYouAre}</Para>
      </PanelCard>
    </div>
  );
}

function SectionC() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  return (
    <div data-testid="section-c">
      <SectionLabel letter="C" title="How Your Strengths Connect to Your Development" color={C.gold} />
      <div
        style={{
          background: `linear-gradient(135deg, ${C.forestPale} 0%, ${C.goldLight} 100%)`,
          borderLeft: `3pt solid ${C.gold}`,
          border: `0.7pt solid ${C.line}`,
          padding: "14px 18px",
        }}
      >
        <Label color={C.goldDark}>Reflection Point · The Developmental Arc</Label>
        <Para size={9}>{SAMPLE.arc}</Para>
      </div>
    </div>
  );
}

function SectionD() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  return (
    <div data-testid="section-d">
      <SectionLabel letter="D" title="Named Skill Gaps (Five Prioritised — Why / Action / Evidence)" color={C.gold} />
      <div style={{ display: "grid", gap: "10px" }}>
        {SAMPLE.gaps.map((g, i) => (
          <PanelCard key={i} leftBorder={C.gold}>
            <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
              <NumberBubble n={i + 1} color={C.gold} />
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px", flexWrap: "wrap" }}>
                  <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(10.5), color: C.charcoal }}>
                    {g.name}
                  </div>
                  <Pill fg={C.goldDark} bg={C.goldLight} border={C.gold}>{g.category}</Pill>
                </div>
                <div style={{ marginTop: "10px", display: "grid", gridTemplateColumns: "90px 1fr", rowGap: "8px", columnGap: "12px" }}>
                  <Label color={C.forest}>Why</Label>
                  <Para size={9}>{g.why}</Para>
                  <Label color={C.goldDark}>Action</Label>
                  <Para size={9}>{g.action}</Para>
                  <Label color={C.charcoal}>Evidence</Label>
                  <Para size={9}>{g.evidence}</Para>
                </div>
              </div>
            </div>
          </PanelCard>
        ))}
      </div>
    </div>
  );
}

function SectionE() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  return (
    <div data-testid="section-e">
      <SectionLabel letter="E" title="Sequenced Development Pathway" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" }}>
        {SAMPLE.pathway.phases.map((p, i) => (
          <PanelCard key={i} bg="#FFFFFF" leftBorder={null} style={{ borderTop: `3pt solid ${C.forest}` }}>
            <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(10), color: C.forest, marginBottom: "6px" }}>
              {p.label}
            </div>
            <ul style={{ margin: "0 0 0 14px", padding: 0, fontFamily: FONT_BODY, fontSize: pt(8.5), color: C.charcoal, lineHeight: 1.5 }}>
              {p.items.map((it, j) => <li key={j} style={{ marginBottom: "4px" }}>{it}</li>)}
            </ul>
          </PanelCard>
        ))}
      </div>
      <div style={{ marginTop: "14px" }}>
        <PanelCard leftBorder={C.forest} bg={C.ivory}>
          <Label color={C.forest}>Daily &amp; Weekly Learning Modalities (Beyond Credentials)</Label>
          <ul style={{ margin: "6px 0 0 16px", padding: 0, fontFamily: FONT_BODY, fontSize: pt(9), color: C.charcoal, lineHeight: 1.55 }}>
            {SAMPLE.pathway.modalities.map((m, i) => <li key={i} style={{ marginBottom: "4px" }}>{m}</li>)}
          </ul>
        </PanelCard>
      </div>
    </div>
  );
}

function SectionF() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  const inv = (lv) =>
    lv === "Low" ? { fg: C.forest, bg: C.forestPale, border: C.forestMid } :
    lv === "Moderate" ? { fg: C.goldDark, bg: C.goldLight, border: C.gold } :
    { fg: "#7a1f1f", bg: "#FBE9E9", border: "#E0A2A2" };
  return (
    <div data-testid="section-f">
      <SectionLabel letter="F" title="Named Credentials & Training Providers" color={C.gold} />
      {/* Caveat panel */}
      <div
        style={{
          backgroundColor: C.amberSoft,
          border: `0.7pt solid ${C.goldDark}`,
          padding: "12px 14px",
          marginBottom: "12px",
          display: "flex",
          gap: "12px",
          alignItems: "flex-start",
        }}
      >
        <div
          style={{
            width: "22px", height: "22px", borderRadius: "50%",
            backgroundColor: C.goldDark, color: "#FFFFFF",
            fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(10),
            display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 22px",
          }}
        >?</div>
        <div>
          <Label color={C.goldDark}>Credential Verification Caveat</Label>
          <Para size={9}>{SAMPLE.credentials.caveat}</Para>
        </div>
      </div>
      <div style={{ display: "grid", gap: "10px" }}>
        {SAMPLE.credentials.items.map((c, i) => {
          const tag = inv(c.investment);
          return (
            <PanelCard key={i}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 130px", gap: "10px", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(11), color: C.charcoal }}>{c.name}</div>
                  <div style={{ marginTop: "4px", fontFamily: FONT_BODY, fontSize: pt(8.5), color: C.charcoalSoft }}>
                    Provider: <strong style={{ color: C.charcoal }}>{c.provider}</strong> · Time: {c.time}
                  </div>
                  <div style={{ marginTop: "6px", fontFamily: FONT_BODY, fontSize: pt(8.5), color: C.charcoalSoft }}>
                    Eligibility: {c.eligibility}
                  </div>
                  <div style={{ marginTop: "6px", fontFamily: FONT_BODY, fontStyle: "italic", fontSize: pt(8), color: C.muted }}>
                    {c.note}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <Pill fg={tag.fg} bg={tag.bg} border={tag.border}>Investment: {c.investment}</Pill>
                </div>
              </div>
            </PanelCard>
          );
        })}
      </div>
    </div>
  );
}

function SectionG() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  return (
    <div data-testid="section-g">
      <SectionLabel letter="G" title="AI Proficiency Pathway" />
      <PanelCard leftBorder={null} bg={C.forestPale}>
        <Para size={9}>{SAMPLE.aiProficiency}</Para>
      </PanelCard>
    </div>
  );
}

function SectionH() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  return (
    <div data-testid="section-h">
      <SectionLabel letter="H" title="Working Approach Development" />
      <PanelCard>
        <Para size={9}>{SAMPLE.workingApproach}</Para>
      </PanelCard>
    </div>
  );
}

function SectionI() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  return (
    <div data-testid="section-i">
      <SectionLabel letter="I" title="Structural & Operating Model Actions" />
      <PanelCard bg={C.forestPale}>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(180px, 28%) 1fr", rowGap: "8px", columnGap: "16px" }}>
          {SAMPLE.structural.map(([k, v], i) => (
            <React.Fragment key={i}>
              <div style={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: pt(8), textTransform: "uppercase", letterSpacing: "0.5px", color: C.forest, paddingTop: "2px" }}>{k}</div>
              <div style={{ fontFamily: FONT_BODY, fontSize: pt(9), color: C.charcoal, lineHeight: 1.55 }}>{v}</div>
            </React.Fragment>
          ))}
        </div>
      </PanelCard>
    </div>
  );
}

function SectionJ() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  return (
    <div data-testid="section-j">
      <SectionLabel letter="J" title="Knowledge Depth Track" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
        {SAMPLE.knowledge.map((k, i) => (
          <div key={i} style={{ backgroundColor: C.forestPale, border: `0.7pt solid ${C.line}`, borderTop: `3pt solid ${C.forest}`, padding: "12px 14px" }}>
            <div style={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: pt(7.5), letterSpacing: "0.5px", textTransform: "uppercase", color: C.goldDark }}>
              {k.label}
            </div>
            <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(10.5), color: C.charcoal, marginTop: "4px" }}>
              {k.name}
            </div>
            <div style={{ marginTop: "8px" }}>
              <Label color={C.forest}>Vehicle</Label>
              <Para size={8.5}>{k.vehicle}</Para>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionK() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  return (
    <div data-testid="section-k">
      <SectionLabel letter="K" title="Relationship and Influence Map" />
      <div style={{ border: `0.7pt solid ${C.line}` }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.6fr 2fr 1fr", backgroundColor: C.forest, color: "#FFFFFF" }}>
          {["Stakeholder / Function", "Purpose", "Cadence"].map((h, i) => (
            <div key={i} style={{ padding: "8px 12px", fontFamily: FONT_BODY, fontWeight: 700, fontSize: pt(8), textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</div>
          ))}
        </div>
        {SAMPLE.relationships.map((row, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1.6fr 2fr 1fr", borderTop: `0.7pt solid ${C.lineSoft}`, backgroundColor: i % 2 ? C.ivory : "#FFFFFF" }}>
            {row.map((cell, j) => (
              <div key={j} style={{ padding: "10px 12px", fontFamily: FONT_BODY, fontSize: pt(8.5), color: C.charcoal, lineHeight: 1.5 }}>{cell}</div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionL() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  return (
    <div data-testid="section-l">
      <SectionLabel letter="L" title="Portfolio / Work Product Track" />
      <div style={{ display: "grid", gap: "10px" }}>
        {SAMPLE.portfolio.map((p, i) => (
          <PanelCard key={i} leftBorder={C.charcoalSoft}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 130px", gap: "14px", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(10.5), color: C.charcoal }}>{p.name}</div>
                <div style={{ marginTop: "6px" }}>
                  <Label color={C.forest}>Scope</Label>
                  <Para size={8.5}>{p.scope}</Para>
                </div>
                <div style={{ marginTop: "6px" }}>
                  <Label color={C.forest}>Audience</Label>
                  <Para size={8.5}>{p.audience}</Para>
                </div>
              </div>
              <div style={{ border: `1px solid ${C.gold}`, backgroundColor: C.goldLight, padding: "10px", textAlign: "center" }}>
                <div style={{ fontFamily: FONT_BODY, fontSize: pt(7.5), letterSpacing: "0.5px", textTransform: "uppercase", color: C.goldDark, fontWeight: 700 }}>Target</div>
                <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(10), color: C.charcoal, marginTop: "4px" }}>{p.target}</div>
              </div>
            </div>
          </PanelCard>
        ))}
      </div>
    </div>
  );
}

function SectionM() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  return (
    <div data-testid="section-m">
      <SectionLabel letter="M" title="Funding and Time Commitment from Sagicor" />
      <PanelCard bg={C.forestPale}>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(180px, 28%) 1fr", rowGap: "10px", columnGap: "16px" }}>
          {SAMPLE.funding.map(([k, v], i) => (
            <React.Fragment key={i}>
              <div style={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: pt(8), textTransform: "uppercase", letterSpacing: "0.5px", color: C.forest, paddingTop: "2px" }}>{k}</div>
              <div style={{ fontFamily: FONT_BODY, fontSize: pt(9), color: C.charcoal, lineHeight: 1.55 }}>{v}</div>
            </React.Fragment>
          ))}
        </div>
      </PanelCard>
    </div>
  );
}

function SectionN() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  return (
    <div data-testid="section-n">
      <SectionLabel letter="N" title="Retention and Recognition Signal" color={C.gold} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
        {SAMPLE.recognition.map((r, i) => (
          <PanelCard key={i} leftBorder={C.gold}>
            <div style={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: pt(7.5), letterSpacing: "0.5px", textTransform: "uppercase", color: C.goldDark }}>{r.label}</div>
            <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(11), color: C.charcoal, marginTop: "4px" }}>{r.headline}</div>
            {r.body ? <div style={{ marginTop: "6px" }}><Para size={9}>{r.body}</Para></div> : null}
            {r.items ? (
              <ul style={{ margin: "8px 0 0 16px", padding: 0, fontFamily: FONT_BODY, fontSize: pt(9), color: C.charcoal, lineHeight: 1.5 }}>
                {r.items.map((it, j) => <li key={j} style={{ marginBottom: "4px" }}>{it}</li>)}
              </ul>
            ) : null}
          </PanelCard>
        ))}
      </div>
    </div>
  );
}

function SectionO() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  return (
    <div data-testid="section-o">
      <SectionLabel letter="O" title="Career Horizon" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
        {SAMPLE.horizon.map((h, i) => {
          const horizonBg = h.bg || (i === 0 ? C.forestPale : (i === 1 ? C.ivory : "#FFFFFF"));
          const underlineColor = h.underline?.color || (i === 0 ? C.forest : (i === 1 ? C.gold : C.mutedLight));
          const underlineStyle = h.underline?.style || (i === 0 ? "solid" : (i === 1 ? "dashed" : "dotted"));
          return (
          <div key={i} style={{ backgroundColor: horizonBg, border: `0.7pt solid ${C.line}`, padding: "14px 16px" }}>
            <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(14), color: C.forestDark, borderBottom: `2px ${underlineStyle} ${underlineColor}`, paddingBottom: "6px", display: "inline-block" }}>
              {h.year}
            </div>
            <div style={{ marginTop: "6px", fontFamily: FONT_BODY, fontWeight: 700, fontSize: pt(7.5), letterSpacing: "0.5px", textTransform: "uppercase", color: i === 1 ? C.goldDark : (i === 2 ? C.muted : C.forest) }}>
              {h.label}
            </div>
            <div style={{ marginTop: "8px" }}><Para size={9}>{h.body}</Para></div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

function SectionP() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  return (
    <div data-testid="section-p">
      <SectionLabel letter="P" title="What Could Slow This Down" color={C.goldDark} />
      <div style={{ display: "grid", gap: "10px" }}>
        {SAMPLE.risks.map((r, i) => (
          <PanelCard key={i} leftBorder={C.goldDark}>
            <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
              <NumberBubble n={i + 1} color={C.goldDark} />
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(10.5), color: C.charcoal }}>{r.name}</div>
                <div style={{ marginTop: "10px", display: "grid", gridTemplateColumns: "100px 1fr", rowGap: "8px", columnGap: "12px" }}>
                  <Label color={C.goldDark}>Signal</Label>
                  <Para size={9}>{r.signal}</Para>
                  <Label color={C.forest}>Mitigation</Label>
                  <Para size={9}>{r.mitigation}</Para>
                  <Label color={C.charcoal}>Escalation</Label>
                  <Para size={9}>{r.escalation}</Para>
                </div>
              </div>
            </div>
          </PanelCard>
        ))}
      </div>
    </div>
  );
}

function SectionQ() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  return (
    <div data-testid="section-q">
      <SectionLabel letter="Q" title="Cross-Functional Stretch Assignments" />
      <div style={{ display: "grid", gap: "10px" }}>
        {SAMPLE.stretches.items.map((s, i) => (
          <PanelCard key={i} leftBorder={C.forest}>
            <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
              <NumberBubble n={i + 1} color={C.forest} />
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(10.5), color: C.charcoal }}>{s.name}</div>
                <div style={{ marginTop: "10px", display: "grid", gridTemplateColumns: "100px 1fr", rowGap: "8px", columnGap: "12px" }}>
                  <Label color={C.forest}>Allocation</Label>
                  <Para size={9}>{s.allocation}</Para>
                  <Label color={C.goldDark}>Outcome</Label>
                  <Para size={9}>{s.outcome}</Para>
                  <Label color={C.charcoal}>Host</Label>
                  <Para size={9}>{s.host}</Para>
                </div>
              </div>
            </div>
          </PanelCard>
        ))}
      </div>
      <div style={{ marginTop: "14px" }}>
        <PanelCard leftBorder={C.gold} bg={C.ivory}>
          <Label color={C.goldDark}>Lighter-Weight Cross-Functional Exposures</Label>
          <ul style={{ margin: "6px 0 0 16px", padding: 0, fontFamily: FONT_BODY, fontSize: pt(9), color: C.charcoal, lineHeight: 1.55 }}>
            {SAMPLE.stretches.lighter.map((l, i) => <li key={i} style={{ marginBottom: "4px" }}>{l}</li>)}
          </ul>
        </PanelCard>
      </div>
    </div>
  );
}

function SectionR() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  return (
    <div data-testid="section-r">
      <SectionLabel letter="R" title="Recognition Milestones" color={C.gold} />
      <div style={{ display: "grid", gap: "10px" }}>
        {SAMPLE.milestones.map((m, i) => (
          <PanelCard key={i}>
            <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", gap: "14px", alignItems: "stretch" }}>
              <div style={{ border: `1px solid ${C.gold}`, backgroundColor: C.goldLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(16), color: C.goldDark }}>{m.q}</div>
              </div>
              <div>
                <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(11), color: C.charcoal }}>{m.name}</div>
                <div style={{ marginTop: "8px", display: "grid", gridTemplateColumns: "90px 1fr", rowGap: "6px", columnGap: "12px" }}>
                  <Label color={C.forest}>Audience</Label>
                  <Para size={9}>{m.audience}</Para>
                  <Label color={C.goldDark}>Form</Label>
                  <Para size={9}>{m.form}</Para>
                  <Label color={C.charcoal}>Trigger</Label>
                  <Para size={9}>{m.trigger}</Para>
                </div>
              </div>
            </div>
          </PanelCard>
        ))}
      </div>
    </div>
  );
}

function SectionS() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  return (
    <div data-testid="section-s">
      <SectionLabel letter="S" title="Peer Cohort Identification" />
      <div style={{ backgroundColor: C.forestPale, border: `1pt solid ${C.forest}`, padding: "16px 18px" }}>
        <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(14), color: C.forestDark }}>
          {SAMPLE.cohort.identity}
        </div>
        <div style={{ marginTop: "8px" }}><Para size={9}>{SAMPLE.cohort.summary}</Para></div>
        <div style={{ marginTop: "14px", display: "grid", gridTemplateColumns: "minmax(180px, 28%) 1fr", rowGap: "10px", columnGap: "16px" }}>
          {SAMPLE.cohort.cadence.map(([k, v], i) => (
            <React.Fragment key={i}>
              <div style={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: pt(8), textTransform: "uppercase", letterSpacing: "0.5px", color: C.forest, paddingTop: "2px" }}>{k}</div>
              <div style={{ fontFamily: FONT_BODY, fontSize: pt(9), color: C.charcoal, lineHeight: 1.55 }}>{v}</div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

function SectionT() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  return (
    <div data-testid="section-t">
      <SectionLabel letter="T" title="Measurement and Re-Assessment" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px" }}>
        {SAMPLE.measurement.map((m, i) => {
          const isLast = i === SAMPLE.measurement.length - 1;
          return (
            <div
              key={i}
              style={{
                border: `0.7pt solid ${C.line}`,
                padding: "12px 14px",
                backgroundColor: "#FFFFFF",
                gridColumn: isLast ? "1 / -1" : "auto",
              }}
            >
              <Label color={C.goldDark}>{m.label}</Label>
              <Para size={9}>{m.body}</Para>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// -------------------- Visualisation 1: Score Composition & Evidence Wheel --------------------
function ScoreCompositionWheel() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  const v = MORGAN_VIS.composition;
  // Circumference = 2π·52 ≈ 326.7. Layer wedges by weight (MV 30→98, TA 25→82, HOS 25→82, TSA 20→65).
  // For Alex: ONLY TA is observed; MV / HOS / TSA inferred (gold).
  // Layer geometry: starts at -90° (top), clockwise. dashoffset = cumulative.
  const arcs = {
    MV:  { len: 98, off: 0,    sourceColor: "#C28F2C", labelColor: "#9B6F1F", labelText: "MV · INF",  scoreLen: Math.round((60/100)*98) },
    TA:  { len: 82, off: -98,  sourceColor: "#00583D", labelColor: "#1F2933", labelText: "TA · OBS",  scoreLen: Math.round((68/100)*82) },
    HOS: { len: 82, off: -180, sourceColor: "#C28F2C", labelColor: "#9B6F1F", labelText: "HOS · INF", scoreLen: Math.round((55/100)*82) },
    TSA: { len: 65, off: -262, sourceColor: "#C28F2C", labelColor: "#9B6F1F", labelText: "TSA · INF", scoreLen: Math.round((62/100)*65) },
  };
  const Cf = 327; // full dasharray complement

  const fillForLayer = (layer) => {
    if (layer.source === "OBS" && layer.key === "SA") return "bar-fill-muted";
    if (layer.source === "OBS") return "";
    return "gold";
  };

  return (
    <div className="viz-section" data-testid="viz-score-composition">
      <h2><span className="sec-num">2</span>Score Composition &amp; Evidence Wheel</h2>
      <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: "16px", alignItems: "center" }}>
        {/* Donut */}
        <div style={{ textAlign: "center" }}>
          <svg viewBox="0 0 170 170" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "auto" }}>
            <circle cx="85" cy="85" r="64" fill="none" stroke="#E8E5DC" strokeWidth="1" />
            {/* Layer-weight ring (low opacity) then score-level overlay (full opacity) per layer */}
            {Object.entries(arcs).map(([k, a]) => (
              <g key={k}>
                <circle cx="85" cy="85" r="52" fill="none"
                  stroke={a.sourceColor} strokeWidth="22"
                  strokeDasharray={`${a.len} ${Cf - a.len}`}
                  strokeDashoffset={a.off}
                  transform="rotate(-90 85 85)"
                  opacity={a.sourceColor === "#00583D" ? 0.42 : 0.25}
                />
                <circle cx="85" cy="85" r="52" fill="none"
                  stroke={a.sourceColor} strokeWidth="22"
                  strokeDasharray={`${a.scoreLen} ${Cf - a.scoreLen}`}
                  strokeDashoffset={a.off}
                  transform="rotate(-90 85 85)"
                  opacity={a.sourceColor === "#00583D" ? 1 : 0.62}
                />
              </g>
            ))}
            {/* Inner BRI display */}
            <circle cx="85" cy="85" r="36" fill="white" stroke="#D6D2C7" strokeWidth="0.5" />
            <text x="85" y="78" textAnchor="middle" fontFamily="Cambria" fontSize="7" fill="#6B7280" letterSpacing="1">BRI</text>
            <text x="85" y="98" textAnchor="middle" fontFamily="Cambria" fontSize="20" fontWeight="700" fill="#003D29">{typeof v.bri === "object" ? v.bri.score : v.bri}</text>
            {/* External labels */}
            <text x="85" y="14"  textAnchor="middle" fontFamily="Calibri" fontSize="6.5" fontWeight="700" fill={arcs.MV.labelColor}  letterSpacing="0.5">{arcs.MV.labelText}</text>
            <text x="158" y="98" textAnchor="middle" fontFamily="Calibri" fontSize="6.5" fontWeight="700" fill={arcs.TA.labelColor}  letterSpacing="0.5">{arcs.TA.labelText}</text>
            <text x="85" y="166" textAnchor="middle" fontFamily="Calibri" fontSize="6.5" fontWeight="700" fill={arcs.HOS.labelColor} letterSpacing="0.5">{arcs.HOS.labelText}</text>
            <text x="12" y="98"  textAnchor="middle" fontFamily="Calibri" fontSize="6.5" fontWeight="700" fill={arcs.TSA.labelColor} letterSpacing="0.5">{arcs.TSA.labelText}</text>
          </svg>
          <div style={{ fontSize: "6.5pt", letterSpacing: "0.5px", color: "#6B7280", marginTop: "4px" }}>
            Forest = Observed · Gold = Inferred<br/>Light tone = layer weight · Solid = score level
          </div>
        </div>

        {/* Composition table */}
        <table className="composition-table">
          <tbody>
            {v.layers.map((L) => (
              <tr key={L.key}>
                <td className="layer-name">
                  <strong>{L.name}</strong>
                  {L.note ? <><br/><span className="viz-muted-text viz-tiny">{L.note}</span></> : null}
                </td>
                <td className="layer-weight">{L.weight}%</td>
                <td className="layer-bar">
                  <div className="viz-bar">
                    <div className={`viz-bar-fill ${fillForLayer(L)}`.trim()} style={{ width: `${L.score}%` }} />
                  </div>
                </td>
                <td className="layer-source">
                  <span className={`viz-tag ${L.source === "OBS" ? "obs" : "inf"}`}>{L.source === "OBS" ? "Obs" : "Inf"}</span>
                </td>
                <td className="layer-score">{L.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: "12px", paddingTop: "10px", borderTop: "0.5pt solid #E8E5DC", fontSize: "7.5pt", color: "#6B7280", lineHeight: 1.5 }}>
        {v.reconciliation}
      </div>
    </div>
  );
}

// -------------------- Visualisation 2: Four-Lens Audit Radar --------------------
function FourLensRadar() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  const v = MORGAN_VIS.fourLens;
  return (
    <div className="viz-section" data-testid="viz-four-lens-radar">
      <h2><span className="sec-num">4</span>Four-Lens Audit (Based on Observed Evidence)</h2>
      <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: "18px", alignItems: "center" }}>
        {/* Radar */}
        <div style={{ textAlign: "center" }}>
          <svg viewBox="0 0 220 220" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "auto" }}>
            <g transform="translate(110 110)">
              {/* Concentric reference rings at 25 / 50 / 75 / 100 */}
              <polygon points="0,-25 25,0 0,25 -25,0"     fill="none" stroke="#E8E5DC" strokeWidth="0.6" />
              <polygon points="0,-50 50,0 0,50 -50,0"     fill="none" stroke="#E8E5DC" strokeWidth="0.6" />
              <polygon points="0,-75 75,0 0,75 -75,0"     fill="none" stroke="#C28F2C" strokeWidth="1.2" strokeDasharray="3,2" opacity="0.65" />
              <polygon points="0,-100 100,0 0,100 -100,0" fill="none" stroke="#D6D2C7" strokeWidth="0.6" />
              {/* Axes */}
              <line x1="0" y1="0" x2="0"    y2="-100" stroke="#D6D2C7" strokeWidth="0.5" />
              <line x1="0" y1="0" x2="100"  y2="0"    stroke="#D6D2C7" strokeWidth="0.5" />
              <line x1="0" y1="0" x2="0"    y2="100"  stroke="#D6D2C7" strokeWidth="0.5" />
              <line x1="0" y1="0" x2="-100" y2="0"    stroke="#D6D2C7" strokeWidth="0.5" />
              {/* Alex polygon: Autonomy 62 N, Vision 58 E, Output 68 S, Working 70 W */}
              <polygon points={v.polygon} fill="#00583D" fillOpacity="0.22" stroke="#00583D" strokeWidth="1.6" />
              {/* Score dots — Vision is Thin Evidence (gold), others forest */}
              <circle cx="0"   cy="-62" r="3" fill="#00583D" />
              <circle cx="58"  cy="0"   r="3" fill="#C28F2C" stroke="#9B6F1F" strokeWidth="0.8" />
              <circle cx="0"   cy="68"  r="3" fill="#00583D" />
              <circle cx="-70" cy="0"   r="3" fill="#00583D" />
              {/* Builder threshold tick on each axis */}
              <line x1="-3" y1="75" x2="3" y2="75" stroke="#C28F2C" strokeWidth="1.5" />
              {/* Axis labels */}
              <text x="0"    y="-108" textAnchor="middle" fontFamily="Cambria" fontSize="9" fontWeight="700" fill="#003D29">AUTONOMY</text>
              <text x="0"    y="-100" textAnchor="middle" fontFamily="Calibri" fontSize="7" fill="#6B7280">62</text>
              <text x="108"  y="-3"   textAnchor="start"  fontFamily="Cambria" fontSize="9" fontWeight="700" fill="#003D29">VISION</text>
              <text x="108"  y="9"    textAnchor="start"  fontFamily="Calibri" fontSize="7" fill="#9B6F1F">58 · thin</text>
              <text x="0"    y="118"  textAnchor="middle" fontFamily="Cambria" fontSize="9" fontWeight="700" fill="#003D29">OUTPUT</text>
              <text x="0"    y="128"  textAnchor="middle" fontFamily="Calibri" fontSize="7" fill="#6B7280">68</text>
              <text x="-108" y="-3"   textAnchor="end"    fontFamily="Cambria" fontSize="9" fontWeight="700" fill="#003D29">WORKING</text>
              <text x="-108" y="9"    textAnchor="end"    fontFamily="Calibri" fontSize="7" fill="#6B7280">70</text>
            </g>
          </svg>
          <div style={{ marginTop: "4px", fontSize: "6.5pt", letterSpacing: "0.5px", color: "#6B7280" }}>
            <span style={{ color: "#00583D", fontWeight: 700 }}>━━</span> Capability Profile<br/>
            <span style={{ color: "#C28F2C", fontWeight: 700 }}>- -</span> Builder Threshold (75)
          </div>
        </div>

        {/* Lens descriptions */}
        <div>
          {v.lenses.map((L) => (
            <div key={L.name} className="lens-item">
              <div className="lens-name">{L.name}</div>
              <div className={`lens-rating-tag ${L.ratingClass}`}>{L.rating}</div>
              <div className="lens-text">{L.body}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Cadre-relative threshold footnote (Methodology §4.4) */}
      <div
        data-testid="radar-footnote"
        style={{
          marginTop: "10px",
          paddingTop: "8px",
          borderTop: "0.5pt solid #E8E5DC",
          fontFamily: FONT_BODY,
          fontStyle: "italic",
          fontSize: "7.5pt",
          color: "#6B7280",
          lineHeight: 1.5,
        }}
      >
        Builder Threshold shown is the Senior IC reference (75). Cadre-relative threshold per Methodology §4.4.
      </div>
    </div>
  );
}

// -------------------- Visualisation 3: AI Posture Matrix --------------------
function AIPostureMatrix() {
  const { data: SAMPLE, vis: MORGAN_VIS } = useXray();
  const v = MORGAN_VIS.aiPosture;
  return (
    <div className="viz-section" data-testid="viz-ai-posture-matrix">
      <h2><span className="sec-num">5</span>AI Posture Matrix</h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", alignItems: "stretch" }}>
        {/* Matrix */}
        <div style={{ background: "#FAFAF7", border: "1pt solid #D6D2C7", padding: "14px" }}>
          <div className="viz-label" style={{ marginBottom: "8px" }}>AI Posture Matrix</div>
          <svg viewBox="0 0 240 220" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "auto" }}>
            {/* Quadrant backgrounds */}
            <rect x="30"  y="105" width="100" height="75" fill="#F4E5C0" opacity="0.35" />
            <rect x="130" y="105" width="100" height="75" fill="#F4D8D1" opacity="0.55" />
            <rect x="30"  y="30"  width="100" height="75" fill="#DDEBE3" opacity="0.7"  />
            <rect x="130" y="30"  width="100" height="75" fill="#F4E5C0" opacity="0.45" />
            {/* Labels */}
            <text x="80"  y="48"  textAnchor="middle" fontFamily="Cambria" fontSize="7" fontWeight="700" fill="#003D29">SAFE &amp; READY</text>
            <text x="80"  y="58"  textAnchor="middle" fontFamily="Calibri" fontSize="6" fill="#1A6B52">Champion role</text>
            <text x="180" y="48"  textAnchor="middle" fontFamily="Cambria" fontSize="7" fontWeight="700" fill="#9B6F1F">AUGMENT</text>
            <text x="180" y="58"  textAnchor="middle" fontFamily="Calibri" fontSize="6" fill="#9B6F1F">Displacement-aware</text>
            <text x="80"  y="155" textAnchor="middle" fontFamily="Cambria" fontSize="7" fontWeight="700" fill="#9B6F1F">DEVELOP</text>
            <text x="80"  y="165" textAnchor="middle" fontFamily="Calibri" fontSize="6" fill="#9B6F1F">Fundamentals first</text>
            <text x="180" y="155" textAnchor="middle" fontFamily="Cambria" fontSize="7" fontWeight="700" fill="#B5132C">RESKILL</text>
            <text x="180" y="165" textAnchor="middle" fontFamily="Calibri" fontSize="6" fill="#B5132C">Priority intervention</text>
            {/* Frame */}
            <rect x="30" y="30" width="200" height="150" fill="none" stroke="#D6D2C7" strokeWidth="0.8" />
            <line x1="130" y1="30"  x2="130" y2="180" stroke="#D6D2C7" strokeWidth="0.6" />
            <line x1="30"  y1="105" x2="230" y2="105" stroke="#D6D2C7" strokeWidth="0.6" />
            {/* X axis */}
            <line x1="30" y1="180" x2="230" y2="180" stroke="#6B7280" strokeWidth="0.6" />
            <text x="30"  y="195" textAnchor="middle" fontFamily="Calibri" fontSize="6.5" fill="#6B7280">Low</text>
            <text x="130" y="195" textAnchor="middle" fontFamily="Calibri" fontSize="6.5" fill="#6B7280">50</text>
            <text x="230" y="195" textAnchor="middle" fontFamily="Calibri" fontSize="6.5" fill="#6B7280">Critical</text>
            <text x="130" y="208" textAnchor="middle" fontFamily="Cambria" fontSize="7.5" fontWeight="700" fill="#1F2933">AI DISPLACEMENT RISK →</text>
            {/* Y axis label */}
            <text x="20" y="105" textAnchor="middle" fontFamily="Calibri" fontSize="6.5" fill="#6B7280" transform="rotate(-90 20 105)">Ready Now ↑ Significant Lift</text>
            <text x="14" y="105" textAnchor="middle" fontFamily="Cambria" fontSize="7.5" fontWeight="700" fill="#1F2933" transform="rotate(-90 14 105)">AI EMBEDDING</text>
            {/* Cohort reference dots */}
            {[[65,68],[92,85],[118,92],[146,125],[178,140],[190,158],[55,142],[105,128]].map(([cx,cy], i) => (
              <circle key={i} cx={cx} cy={cy} r="2" fill="#6B7280" opacity="0.4" />
            ))}
            {/* Alex's dot — DEVELOP quadrant */}
            <circle cx={v.dot.cx} cy={v.dot.cy} r="6" fill="#00583D" stroke="white" strokeWidth="2" />
            <text x={v.dot.cx} y={v.dot.cy - 12} textAnchor="middle" fontFamily="Cambria" fontSize="7" fontWeight="700" fill="#003D29">{v.dot.label}</text>
          </svg>
          <div style={{ marginTop: "6px", fontSize: "7pt", color: "#6B7280", lineHeight: 1.4 }}>
            <strong style={{ color: "#003D29" }}>Quadrant: {v.quadrant}.</strong> {v.quadrantSummary}
          </div>
        </div>

        {/* Dual band cards */}
        <div style={{ display: "grid", gridTemplateRows: "1fr 1fr", gap: "14px" }}>
          <div className="band-card">
            <div className="viz-label">AI Displacement Risk</div>
            <div className={`band-tag ${v.displacement.bandClass}`}>{v.displacement.band} · Score {v.displacement.score}</div>
            <div className="band-detail">{v.displacement.description}</div>
            <div className="signal-mini-grid">
              <div className="signal-mini"><div className="viz-label">Auto. Susc.</div><div className="signal-num">—</div></div>
              <div className="signal-mini"><div className="viz-label">Judgment</div><div className="signal-num">—</div></div>
              <div className="signal-mini"><div className="viz-label">Aug. Lev.</div><div className="signal-num">—</div></div>
              <div className="signal-mini"><div className="viz-label">Tools</div><div className="signal-num">—</div></div>
            </div>
          </div>
          <div className="band-card">
            <div className="viz-label">AI Embedding Readiness</div>
            <div className={`band-tag ${v.embedding.bandClass}`}>{v.embedding.band}</div>
            <div className="band-detail">{v.embedding.description}</div>
            <div style={{ marginTop: "8px", fontSize: "7.5pt", color: "#6B7280" }}>
              <strong style={{ color: "#1F2933" }}>Recommended tools for proficiency:</strong>{" "}
              {v.embedding.tools.join(" · ")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

